import { blobticService } from '../../Commun/services/blobticService';
import { Room, RoomRental, Teacher, Student, CourseTemplate, Group, GroupStudent, Session, Attendance, Invoice, Payment, PaymentMethod, Quiz, QuizQuestion, QuizQuestionOption, QuizQuestionType, QuizLevel, QuizAttempt, AcademyParameter, PlanningControl, AcademyDiscount, DiscountRequest, EmailTemplate, AcademySubscriptionPlan, AcademySubscription, AcademySubscriptionInvoice, SubscriptionUsage, SatimInitiateResponse, SatimConfirmResponse, TeacherAbsenceRequest } from '../types/academy';

/** Calendar date in the user's local timezone (YYYY-MM-DD). Avoids UTC drift from toISOString(). */
export function toLocalDateStr(value?: string | Date | null): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');
}

// boubaker 22/08/2026 - Helpers horaires séances (SSN_START_HOUR / SSN_END_HOUR)
/** Heure locale "HH:mm" depuis un datetime serveur. */
export function toLocalTimeStr(value?: string | Date | null): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Combine une date "YYYY-MM-DD" + une heure "HH:mm" en ISO local "YYYY-MM-DDTHH:mm:ss". */
export function combineLocalDateTime(dateStr?: string | null, timeStr?: string | null): string {
  const d = (dateStr || '').slice(0, 10);
  const tm = (timeStr || '09:00').slice(0, 5);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T${tm}:00` : '';
}

/** Durée en minutes entre deux ISO locaux. */
export function durationMinutes(startIso?: string | null, endIso?: string | null, fallback = 120): number {
  const s = startIso ? new Date(startIso) : null;
  const e = endIso ? new Date(endIso) : null;
  if (!s || !e || Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return fallback;
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / 60000));
}

/** Parses the QST_OPTIONS JSON string returned by the backend (FOR JSON PATH). */
export function parseQuestionOptions(json?: any): QuizQuestionOption[] {
  if (!json) return [];
  try {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((o: any) => o && o.key)
      .map((o: any) => ({
        key: String(o.key),
        label: o.label != null ? String(o.label) : '',
        order: o.order != null ? Number(o.order) : 0,
        isCorrect: !!o.isCorrect,
      }));
  } catch {
    return [];
  }
}

export const mapRoom = (dto: any): Room => ({
  id: dto.ARM_IDF,
  room_number: dto.ARM_ROOM_NUMBER,
  floor: dto.ARM_FLOOR || '',
  capacity: dto.ARM_CAPACITY || 20,
  equipment: typeof dto.ARM_EQUIPMENT === 'string' ? dto.ARM_EQUIPMENT.split(',').map((s: string) => s.trim()).filter(Boolean) : (dto.ARM_EQUIPMENT || []),
  status: dto.ARM_STATUS || 'Active',
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapRoomRental = (dto: any): RoomRental => ({
  id: Number(dto.RNT_IDF || dto.id),
  rnt_uidf: dto.RNT_UIDF,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  arm_idf: Number(dto.ARM_IDF || 0),
  room_number: dto.ARM_ROOM_NUMBER || undefined,
  room_capacity: dto.ARM_CAPACITY ? Number(dto.ARM_CAPACITY) : undefined,
  code: dto.RNT_CODE || '',
  lib: dto.RNT_LIB || '',
  client_name: dto.RNT_CLIENT_NAME || '',
  client_phone: dto.RNT_CLIENT_PHONE || '',
  client_email: dto.RNT_CLIENT_EMAIL || '',
  start_date: dto.RNT_START_DATE ? new Date(dto.RNT_START_DATE).toISOString().slice(0, 16) : '',
  end_date: dto.RNT_END_DATE ? new Date(dto.RNT_END_DATE).toISOString().slice(0, 16) : '',
  price: Number(dto.RNT_PRICE || 0),
  deposit: Number(dto.RNT_DEPOSIT || 0),
  status: (dto.RNT_STATUS || 'CONFIRMED') as any,
  notes: dto.RNT_NOTES || '',
  user_idf: dto.USER_IDF ? Number(dto.USER_IDF) : undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapTeacher = (dto: any): Teacher => {
  const fn = dto.TCH_FIRST_NAME || '';
  const ln = dto.TCH_LAST_NAME || '';
  const full = dto.TCH_FULL_NAME || `${fn} ${ln}`.trim();
  return {
    id: dto.TCH_IDF,
    first_name: fn,
    last_name: ln,
    full_name: full,
    gender: dto.TCH_GENDER,
    date_of_birth: dto.TCH_DATE_OF_BIRTH ? new Date(dto.TCH_DATE_OF_BIRTH).toISOString().split('T')[0] : '',
    email: dto.TCH_EMAIL,
    phone: dto.TCH_PHONE || '',
    specialization: dto.TCH_SPECIALIZATION || '',
    rate_type: dto.TCH_RATE_TYPE || 'hourly',
    hourly_rate: Number(dto.TCH_HOURLY_RATE) || 50,
    hire_date: dto.TCH_HIRE_DATE ? new Date(dto.TCH_HIRE_DATE).toISOString().split('T')[0] : '',
    is_active: Boolean(dto.TCH_IS_ACTIVE),
    bio: dto.TCH_BIO || '',
    avatar_url: dto.TCH_AVATAR_URL,
    photo_base64: dto.TCH_PHOTO_BASE64,
    photo_mime_type: dto.TCH_PHOTO_MIME_TYPE,
    cv_file_name: dto.TCH_CV_FILE_NAME,
    cv_file_base64: dto.TCH_CV_FILE_BASE64,
    cv_file_mime_type: dto.TCH_CV_FILE_MIME_TYPE,
    dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
    portal_active: dto.TCH_PORTAL_ACTIVE != null ? Boolean(Number(dto.TCH_PORTAL_ACTIVE)) : false,
    portal_password: dto.TCH_PORTAL_PASSWORD || '',
    can_take_attendance: dto.TCH_CAN_TAKE_ATTENDANCE != null ? Boolean(Number(dto.TCH_CAN_TAKE_ATTENDANCE)) : false,
  };
};

export const mapStudent = (dto: any): Student => ({
  id: Number(dto.STD_IDF ?? dto.id) || 0,
  category: (dto.STD_CATEGORY as any) || 'adult',
  first_name: dto.STD_FIRST_NAME,
  last_name: dto.STD_LAST_NAME,
  email: dto.STD_EMAIL,
  phone: dto.STD_PHONE || '',
  date_of_birth: dto.STD_DATE_OF_BIRTH ? new Date(dto.STD_DATE_OF_BIRTH).toISOString().split('T')[0] : '',
  emergency_contact: dto.STD_EMERGENCY_CONTACT || '',
  financial_hold: Boolean(dto.STD_FINANCIAL_HOLD),
  hold_since: dto.STD_HOLD_SINCE ? new Date(dto.STD_HOLD_SINCE).toISOString().split('T')[0] : undefined,
  gender: dto.STD_GENDER,
  address: dto.STD_ADDRESS,
  profession: dto.STD_PROFESSION,
  school_level: dto.STD_SCHOOL_LEVEL,
  photo_base64: dto.STD_PHOTO_BASE64,
  photo_mime_type: dto.STD_PHOTO_MIME_TYPE,
  attached_file_name: dto.STD_FILE_NAME,
  attached_file_base64: dto.STD_FILE_BASE64,
  attached_file_mime_type: dto.STD_FILE_MIME_TYPE,
  registration_status: dto.STD_REGISTRATION_STATUS || 'Active',
  crs_idf: dto.CRS_IDF ? Number(dto.CRS_IDF) : undefined,
  time_preference: dto.STD_TIME_PREFERENCE || undefined,
  discount_id: dto.DSC_IDF != null && dto.DSC_IDF !== ''
    ? Number(dto.DSC_IDF)
    : (dto.discount_id != null && dto.discount_id !== '' ? Number(dto.discount_id) : undefined),
  discount_code: dto.DSC_CODE || dto.discount_code || undefined,
  discount_name: dto.DSC_NAME || dto.discount_name || undefined,
  loi_18_07_consent: dto.STD_LOI_18_07_CONSENT != null
    ? Boolean(dto.STD_LOI_18_07_CONSENT)
    : undefined,
  loi_18_07_consent_date: dto.STD_LOI_18_07_CONSENT_DATE
    ? new Date(dto.STD_LOI_18_07_CONSENT_DATE).toLocaleString('fr-DZ')
    : undefined,
  last_updt: dto.LAST_UPDT
    ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ')
    : undefined,
  submitted_date: toLocalDateStr(dto.STD_LOI_18_07_CONSENT_DATE ?? dto.LAST_UPDT),
  portal_active: dto.STD_PORTAL_ACTIVE != null ? Boolean(Number(dto.STD_PORTAL_ACTIVE)) : false,
  portal_password: dto.STD_PORTAL_PASSWORD || '',
});

export const mapCourse = (dto: any): CourseTemplate => ({
  id:          dto.CRS_IDF,
  crs_uidf:    dto.CRS_UIDF  || undefined,
  title:       dto.CRS_TITLE       || '',
  total_hours: Number(dto.CRS_TOTAL_HOURS) || 0,
  price:       Number(dto.CRS_PRICE)       || 0,
  show_price:  dto.CRS_SHOW_PRICE != null ? Boolean(Number(dto.CRS_SHOW_PRICE)) : true,
  description: dto.CRS_DESCRIPTION || '',
  start_date:  dto.CRS_START_DATE  ? new Date(dto.CRS_START_DATE).toISOString().split('T')[0] : undefined,
  end_date:    dto.CRS_END_DATE    ? new Date(dto.CRS_END_DATE).toISOString().split('T')[0]   : undefined,
  age_band:    dto.CRS_AGE_BAND    || undefined,
  age_min:     dto.CRS_AGE_MIN  != null ? Number(dto.CRS_AGE_MIN)  : undefined,
  age_max:     dto.CRS_AGE_MAX  != null ? Number(dto.CRS_AGE_MAX)  : undefined,
  dos_idf:     dto.DOS_IDF      ? Number(dto.DOS_IDF) : undefined,
  dos_code:    dto.DOS_CODE     || undefined,
  dos_lib:     dto.DOS_LIB      || undefined,
  user_idf:    dto.USER_IDF     ? Number(dto.USER_IDF) : undefined,
  user_lib:    dto.USER_LIB     || undefined,
  last_updt:   dto.LAST_UPDT   ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapGroup = (dto: any): Group => ({
  id: dto.GRP_IDF,
  name: dto.GRP_NAME,
  course_id: dto.COURSE_IDF || 1,
  teacher_id: dto.TCH_IDF || 1,
  max_capacity: dto.GRP_MAX_CAPACITY || 20,
  start_date: dto.GRP_START_DATE ? new Date(dto.GRP_START_DATE).toISOString().split('T')[0] : '',
  expected_end_date: dto.GRP_EXPECTED_END_DATE ? new Date(dto.GRP_EXPECTED_END_DATE).toISOString().split('T')[0] : '',
  status: dto.GRP_STATUS || 'Upcoming',
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapGroupStudent = (dto: any): GroupStudent => ({
  id: Number(dto.GST_IDF ?? dto.id) || 0,
  group_id: Number(dto.GRP_IDF ?? dto.group_id) || 0,
  student_id: Number(dto.STD_IDF ?? dto.student_id) || 0,
  enrollment_date: dto.GST_ENROLLMENT_DATE
    ? new Date(dto.GST_ENROLLMENT_DATE).toISOString().split('T')[0]
    : (dto.enrollment_date || ''),
  status: dto.GST_STATUS || dto.status || 'Enrolled'
});

// boubaker 22/08/2026 - mapping séances : datetime complets + champs dérivés (date/start_time/end_time)
// SSN_DAY_OF_WEEK / SSN_IS_RECURRING ne sont plus persistés : dérivés côté client.
export const mapSession = (dto: any): Session => {
  const startIso = dto.SSN_START_HOUR ? toLocalDateStr(dto.SSN_START_HOUR) + 'T' + toLocalTimeStr(dto.SSN_START_HOUR) + ':00' : '';
  const endIso = dto.SSN_END_HOUR ? toLocalDateStr(dto.SSN_END_HOUR) + 'T' + toLocalTimeStr(dto.SSN_END_HOUR) + ':00' : '';
  return {
    id: dto.SSN_IDF,
    group_id: dto.GRP_IDF,
    room_id: dto.ARM_IDF,
    teacher_id: dto.TCH_IDF,
    day_of_week: startIso ? new Date(startIso).getDay() : 0,
    start_hour: startIso,
    end_hour: endIso,
    duration: Number(dto.SSN_DURATION) || durationMinutes(startIso, endIso, 120),
    start_time: toLocalTimeStr(startIso),
    end_time: toLocalTimeStr(endIso),
    date: toLocalDateStr(startIso),
    is_recurring: false,
    parent_session_id: dto.SSN_PARENT_SESSION_IDF,
    attendance_taken: Boolean(Number(dto.SSN_ATTENDANCE_TAKEN)),
    status: dto.SSN_STATUS || 'Scheduled',
    status_reason: dto.SSN_STATUS_REASON || '',
    postponed_date: dto.SSN_POSTPONED_DATE ? toLocalDateStr(dto.SSN_POSTPONED_DATE) : '',
    postponed_time: dto.SSN_POSTPONED_TIME || '',
    teacher_absent: Boolean(Number(dto.SSN_TEACHER_ABSENT))
  };
};

export const mapTeacherAbsenceRequest = (dto: any): TeacherAbsenceRequest => ({
  id: Number(dto.TAR_IDF ?? dto.id),
  uid: dto.TAR_UIDF,
  tch_idf: Number(dto.TCH_IDF ?? dto.tch_idf),
  teacher_name: dto.TEACHER_FULL_NAME || (dto.TCH_FIRST_NAME ? `${dto.TCH_FIRST_NAME} ${dto.TCH_LAST_NAME || ''}`.trim() : (dto.teacher_name || `Enseignant #${dto.TCH_IDF}`)),
  ssn_idf: dto.SSN_IDF ? Number(dto.SSN_IDF) : undefined,
  request_date: dto.TAR_REQUEST_DATE ? toLocalDateStr(dto.TAR_REQUEST_DATE) : (dto.request_date || ''),
  start_date: dto.SSN_START_HOUR ? toLocalDateStr(dto.SSN_START_HOUR) : (dto.TAR_REQUEST_DATE ? toLocalDateStr(dto.TAR_REQUEST_DATE) : (dto.start_date || '')),
  end_date: dto.SSN_END_HOUR ? toLocalDateStr(dto.SSN_END_HOUR) : dto.end_date,
  reason: dto.TAR_REASON || dto.reason || '',
  status: ((dto.TAR_STATUS || dto.status || 'Pending').toUpperCase()) as 'PENDING' | 'APPROVED' | 'REJECTED',
  admin_comment: dto.TAR_DECISION_NOTES || dto.admin_comment || '',
  attachment_name: dto.TAR_ATTACHMENT_NAME || dto.attachment_name || '',
  attachment_base64: dto.TAR_ATTACHMENT_BASE64 || dto.attachment_base64 || '',
  decision_date: dto.TAR_DECISION_DATE || dto.decision_date,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined
});

export const mapAttendance = (dto: any): Attendance => ({
  id: dto.ATD_IDF,
  session_id: dto.SSN_IDF,
  student_id: dto.STD_IDF,
  status: (dto.ATD_STATUS || 'Present') as Attendance['status'],
  check_in_time: dto.ATD_CHECK_IN_TIME
    ? new Date(dto.ATD_CHECK_IN_TIME).toISOString()
    : undefined,
  justified: dto.ATD_JUSTIFIED != null ? Boolean(Number(dto.ATD_JUSTIFIED)) : Boolean(dto.justified),
  justification_reason: dto.ATD_JUSTIFICATION_REASON || dto.justification_reason || '',
  justification_doc_name: dto.ATD_JUSTIFICATION_DOC_NAME || dto.justification_doc_name || '',
  justification_doc_base64: dto.ATD_JUSTIFICATION_DOC_BASE64 || dto.justification_doc_base64 || '',
  justification_date: dto.ATD_JUSTIFICATION_DATE
    ? new Date(dto.ATD_JUSTIFICATION_DATE).toISOString()
    : dto.justification_date
});

export const mapInvoice = (dto: any): Invoice => ({
  id: Number(dto.INV_IDF ?? dto.id) || 0,
  student_id: Number(dto.STD_IDF ?? dto.student_id) || 0,
  group_id: Number(dto.GRP_IDF ?? dto.group_id) || 0,
  invoice_number: dto.INV_NUMBER || dto.invoice_number || '',
  total_amount: Number(dto.INV_TOTAL_AMOUNT ?? dto.total_amount) || 0,
  amount_paid: Number(dto.INV_AMOUNT_PAID ?? dto.amount_paid) || 0,
  issue_date: dto.INV_ISSUE_DATE
    ? new Date(dto.INV_ISSUE_DATE).toISOString().split('T')[0]
    : (dto.issue_date || ''),
  due_date: dto.INV_DUE_DATE
    ? new Date(dto.INV_DUE_DATE).toISOString().split('T')[0]
    : (dto.due_date || ''),
  status: dto.INV_STATUS || dto.status || 'Unpaid',
  discount_id: dto.DSC_IDF != null && dto.DSC_IDF !== ''
    ? Number(dto.DSC_IDF)
    : (dto.discount_id != null && dto.discount_id !== '' ? Number(dto.discount_id) : undefined),
  discount_code: dto.INV_DISCOUNT_CODE || dto.discount_code || undefined,
  discount_percent: Number(dto.INV_DISCOUNT_PERCENT ?? dto.discount_percent) || 0,
  discount_amount: Number(dto.INV_DISCOUNT_AMOUNT ?? dto.discount_amount) || 0,
  original_amount: Number(dto.INV_ORIGINAL_AMOUNT ?? dto.original_amount ?? dto.INV_TOTAL_AMOUNT ?? dto.total_amount) || 0,
  discount_reason: dto.INV_DISCOUNT_REASON || dto.discount_reason || undefined,
  discount_approved: dto.INV_DISCOUNT_APPROVED != null
    ? Boolean(dto.INV_DISCOUNT_APPROVED)
    : (dto.discount_approved != null ? Boolean(dto.discount_approved) : true),
  discount_approved_by: dto.INV_DISCOUNT_APPROVED_BY || dto.discount_approved_by || undefined,
});

export const mapPayment = (dto: any): Payment => ({
  id: Number(dto.PMT_IDF ?? dto.id) || 0,
  invoice_id: Number(dto.INV_IDF ?? dto.invoice_id) || 0,
  student_id: Number(dto.STD_IDF ?? dto.student_id) || 0,
  amount: Number(dto.PMT_AMOUNT ?? dto.amount) || 0,
  payment_date: toLocalDateStr(dto.PMT_DATE ?? dto.payment_date),
  method: dto.PMT_METHOD || dto.method || 'Cash',
  receipt_number: dto.PMT_RECEIPT_NUMBER || dto.receipt_number
});

export const mapQuiz = (dto: any): Quiz => ({
  id: dto.QIZ_IDF,
  code: dto.QIZ_CODE || '',
  title: dto.QIZ_TITLE || '',
  description: dto.QIZ_DESCRIPTION || '',
  duration_minutes: Number(dto.QIZ_DURATION_MINUTES) || 5,
  expiry_days: Number(dto.QIZ_EXPIRY_DAYS) || 7,
  whatsapp: dto.QIZ_WHATSAPP || undefined,
  register_link: dto.QIZ_REGISTER_LINK || undefined,
  display: (dto.QIZ_DISPLAY === 'assistant' ? 'assistant' : 'list') as Quiz['display'],
  auto_next: Boolean(dto.QIZ_AUTO_NEXT),
  QIZ_AUTO_NEXT: Boolean(dto.QIZ_AUTO_NEXT),
  max_attempts_link: Number(dto.QIZ_MAX_ATTEMPTS_LINK) || 50,
  max_attempts_user: Number(dto.QIZ_MAX_ATTEMPTS_USER) || 3,
  QIZ_MAX_ATTEMPTS_LINK: Number(dto.QIZ_MAX_ATTEMPTS_LINK) || 50,
  QIZ_MAX_ATTEMPTS_USER: Number(dto.QIZ_MAX_ATTEMPTS_USER) || 3,
  status: (dto.QIZ_STATUS || 'Draft') as Quiz['status'],
  start_date: dto.QIZ_START_DATE ? new Date(dto.QIZ_START_DATE).toISOString().split('T')[0] : undefined,
  end_date: dto.QIZ_END_DATE ? new Date(dto.QIZ_END_DATE).toISOString().split('T')[0] : undefined,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapQuizAttempt = (dto: any): QuizAttempt => ({
  id: dto.ATT_IDF,
  quiz_id: dto.QIZ_IDF,
  quiz_title: dto.QIZ_TITLE || undefined,
  full_name: dto.ATT_FULL_NAME || '',
  phone: dto.ATT_PHONE || '',
  email: dto.ATT_EMAIL || '',
  age_band: dto.ATT_AGE_BAND || '',
  score: Number(dto.ATT_SCORE) || 0,
  total: Number(dto.ATT_TOTAL) || 0,
  level_code: dto.ATT_LEVEL_CODE || '',
  answers: dto.ATT_ANSWERS || '',
  ref_code: dto.ATT_REF_CODE || '',
  duration_seconds: dto.ATT_DURATION_SECONDS ? Number(dto.ATT_DURATION_SECONDS) : undefined,
  ip_address: dto.ATT_IP_ADDRESS || '',
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapParam = (dto: any): AcademyParameter => ({
  id: dto.PRM_IDF,
  prm_uidf: dto.PRM_UIDF,
  dos_idf: Number(dto.DOS_IDF || 1),
  dos_code: dto.DOS_CODE || '',
  dos_lib: dto.DOS_LIB || '',
  code: dto.PRM_CODE || '',
  value: dto.PRM_VALUE || '',
  lib: dto.PRM_LIB || '',
  type: dto.PRM_TYPE || 'S',
  user_idf: dto.USER_IDF ? Number(dto.USER_IDF) : undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapPlanningControl = (dto: any): PlanningControl => ({
  id: dto.CTL_IDF,
  ctl_uidf: dto.CTL_UIDF,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  code: dto.CTL_CODE || '',
  lib: dto.CTL_LIB || '',
  is_active: Boolean(dto.CTL_IS_ACTIVE),
  is_blocking: Boolean(dto.CTL_IS_BLOCKING),
  description: dto.CTL_DESCRIPTION || '',
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapDiscount = (dto: any): AcademyDiscount => ({
  id: dto.DSC_IDF,
  dsc_uidf: dto.DSC_UIDF,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  code: dto.DSC_CODE || '',
  name: dto.DSC_NAME || '',
  percentage: Number(dto.DSC_PERCENTAGE) || 0,
  requires_validation: Boolean(dto.DSC_REQUIRES_VALIDATION),
  validator_role: dto.DSC_VALIDATOR_ROLE || 'Admin',
  is_auto_applied: Boolean(dto.DSC_IS_AUTO_APPLIED),
  min_courses: dto.DSC_MIN_COURSES != null ? Number(dto.DSC_MIN_COURSES) : 1,
  description: dto.DSC_DESCRIPTION || '',
  is_active: Boolean(dto.DSC_IS_ACTIVE),
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapDiscountRequest = (dto: any): DiscountRequest => ({
  id: Number(dto.REQ_IDF || dto.id),
  req_uidf: dto.REQ_UIDF || dto.req_uidf,
  dos_idf: Number(dto.DOS_IDF || dto.dos_idf || 1),
  dos_code: dto.DOS_CODE || dto.dos_code,
  dos_lib: dto.DOS_LIB || dto.dos_lib,
  invoice_id: Number(dto.INV_IDF || dto.invoice_id),
  invoice_number: dto.INV_NUMBER || dto.invoice_number,
  student_id: Number(dto.STD_IDF || dto.student_id),
  student_name: dto.STD_NAME || dto.student_name,
  student_email: dto.STD_EMAIL || dto.student_email,
  student_phone: dto.STD_PHONE || dto.student_phone,
  discount_id: dto.DSC_IDF ? Number(dto.DSC_IDF) : (dto.discount_id ? Number(dto.discount_id) : undefined),
  discount_code: dto.REQ_DISCOUNT_CODE || dto.discount_code,
  discount_percent: Number(dto.REQ_DISCOUNT_PERCENT ?? dto.discount_percent ?? 0),
  discount_amount: Number(dto.REQ_DISCOUNT_AMOUNT ?? dto.discount_amount ?? 0),
  original_amount: Number(dto.REQ_ORIGINAL_AMOUNT ?? dto.original_amount ?? 0),
  new_amount: Number(dto.REQ_NEW_AMOUNT ?? dto.new_amount ?? 0),
  justification: dto.REQ_JUSTIFICATION || dto.justification || '',
  status: dto.REQ_STATUS || dto.status || 'Pending',
  requested_by_user_id: dto.REQUESTED_BY_USER_IDF ? Number(dto.REQUESTED_BY_USER_IDF) : (dto.requested_by_user_id ? Number(dto.requested_by_user_id) : undefined),
  requested_by_name: dto.REQUESTED_BY_NAME || dto.requested_by_name,
  request_date: toLocalDateStr(dto.REQUEST_DATE || dto.request_date),
  reviewed_by_user_id: dto.REVIEWED_BY_USER_IDF ? Number(dto.REVIEWED_BY_USER_IDF) : (dto.reviewed_by_user_id ? Number(dto.reviewed_by_user_id) : undefined),
  reviewed_by_name: dto.REVIEWED_BY_NAME || dto.reviewed_by_name,
  review_date: dto.REVIEW_DATE ? toLocalDateStr(dto.REVIEW_DATE) : (dto.review_date ? toLocalDateStr(dto.review_date) : undefined),
  review_comment: dto.REVIEW_COMMENT || dto.review_comment,
  last_updt: dto.LAST_UPDT || dto.last_updt
});

export const academyApi = {
  // 0. Dashboard Dynamic KPIs (OKey: Blobtic_Academy,kpi_academy,select)
  getAcademyKpis: async () => {
    try {
      const res = await blobticService.executeZoomRequest<any>({
        ZoomKey: 'kpi_academy',
        Action: {
          OKey: 'Blobtic_Academy,kpi_academy,select'
        },
        UseCache: false
      });

      if (res.success && res.data) {
        const payload = Array.isArray(res.data) ? res.data[0] : res.data;
        const kpiJsonStr = payload?.kpi_json || payload?.kpiJson || '[]';
        const kpiDataDict = payload?.kpi_data || payload?.kpiData || {};

        let kpiList: any[] = [];
        try {
          kpiList = JSON.parse(kpiJsonStr);
        } catch {
          kpiList = [];
        }

        return {
          kpiList,
          kpiData: kpiDataDict
        };
      }
    } catch (err) {
      console.error('Failed to load Academy KPIs from server:', err);
    }
    return null;
  },

  // 1. Rooms
  getRooms: async (): Promise<Room[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_room,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.ARM_IDF,
          room_number: dto.ARM_ROOM_NUMBER,
          floor: dto.ARM_FLOOR || '',
          capacity: dto.ARM_CAPACITY || 20,
          equipment: typeof dto.ARM_EQUIPMENT === 'string' ? dto.ARM_EQUIPMENT.split(',').map((s: string) => s.trim()).filter(Boolean) : (dto.ARM_EQUIPMENT || []),
          status: dto.ARM_STATUS || 'Active',
          dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
          dos_code: dto.DOS_CODE || undefined,
          dos_lib: dto.DOS_LIB || undefined,
          user_lib: dto.USER_LIB || undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load rooms from DB:', err);
    }
    return null;
  },

  createRoom: async (room: Partial<Room>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_room,Insert',
        ARM_ROOM_NUMBER: room.room_number || '',
        ARM_FLOOR: room.floor || '',
        ARM_CAPACITY: room.capacity || 20,
        ARM_EQUIPMENT: Array.isArray(room.equipment) ? room.equipment.join(', ') : (room.equipment || ''),
        ARM_STATUS: room.status || 'Active',
        ...(room.dos_idf ? { DOS_IDF: room.dos_idf } : {})
      }
    });
  },

  updateRoom: async (id: number, room: Partial<Room>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_room,Update',
        ARM_IDF: id,
        ARM_ROOM_NUMBER: room.room_number || '',
        ARM_FLOOR: room.floor || '',
        ARM_CAPACITY: room.capacity || 20,
        ARM_EQUIPMENT: Array.isArray(room.equipment) ? room.equipment.join(', ') : (room.equipment || ''),
        ARM_STATUS: room.status || 'Active',
        ...(room.dos_idf ? { DOS_IDF: room.dos_idf } : {})
      }
    });
  },

  deleteRoom: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_room,Delete',
        ARM_IDF: id
      }
    });
  },

  // 1b. Room Rentals
  getRoomRentals: async (): Promise<RoomRental[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_room_rental,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map(mapRoomRental);
      }
    } catch (err) {
      console.error('Failed to load room rentals from DB:', err);
    }
    return null;
  },

  createRoomRental: async (rental: Partial<RoomRental>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_room_rental,Insert',
        ARM_IDF: rental.arm_idf,
        RNT_CODE: rental.code || '',
        RNT_LIB: rental.lib || '',
        RNT_CLIENT_NAME: rental.client_name || '',
        RNT_CLIENT_PHONE: rental.client_phone || '',
        RNT_CLIENT_EMAIL: rental.client_email || '',
        RNT_START_DATE: rental.start_date,
        RNT_END_DATE: rental.end_date,
        RNT_PRICE: rental.price || 0,
        RNT_DEPOSIT: rental.deposit || 0,
        RNT_STATUS: rental.status || 'CONFIRMED',
        RNT_NOTES: rental.notes || '',
        ...(rental.dos_idf ? { DOS_IDF: rental.dos_idf } : {})
      }
    });
  },

  updateRoomRental: async (id: number, rental: Partial<RoomRental>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_room_rental,Update',
        RNT_IDF: id,
        ARM_IDF: rental.arm_idf,
        RNT_CODE: rental.code || '',
        RNT_LIB: rental.lib || '',
        RNT_CLIENT_NAME: rental.client_name || '',
        RNT_CLIENT_PHONE: rental.client_phone || '',
        RNT_CLIENT_EMAIL: rental.client_email || '',
        RNT_START_DATE: rental.start_date,
        RNT_END_DATE: rental.end_date,
        RNT_PRICE: rental.price || 0,
        RNT_DEPOSIT: rental.deposit || 0,
        RNT_STATUS: rental.status || 'CONFIRMED',
        RNT_NOTES: rental.notes || '',
        ...(rental.dos_idf ? { DOS_IDF: rental.dos_idf } : {})
      }
    });
  },

  deleteRoomRental: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_room_rental,Delete',
        RNT_IDF: id
      }
    });
  },

  // 2. Teachers
  getTeachers: async (): Promise<Teacher[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_teacher,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => {
          const fn = dto.TCH_FIRST_NAME || '';
          const ln = dto.TCH_LAST_NAME || '';
          const full = dto.TCH_FULL_NAME || `${fn} ${ln}`.trim();
          return {
            id: dto.TCH_IDF,
            first_name: fn,
            last_name: ln,
            full_name: full,
            gender: dto.TCH_GENDER,
            date_of_birth: dto.TCH_DATE_OF_BIRTH ? new Date(dto.TCH_DATE_OF_BIRTH).toISOString().split('T')[0] : '',
            email: dto.TCH_EMAIL,
            phone: dto.TCH_PHONE || '',
            specialization: dto.TCH_SPECIALIZATION || '',
            rate_type: dto.TCH_RATE_TYPE || 'hourly',
            hourly_rate: Number(dto.TCH_HOURLY_RATE) || 50,
            hire_date: dto.TCH_HIRE_DATE ? new Date(dto.TCH_HIRE_DATE).toISOString().split('T')[0] : '',
            is_active: Boolean(dto.TCH_IS_ACTIVE),
            bio: dto.TCH_BIO || '',
            avatar_url: dto.TCH_AVATAR_URL,
            photo_base64: dto.TCH_PHOTO_BASE64,
            photo_mime_type: dto.TCH_PHOTO_MIME_TYPE,
            cv_file_name: dto.TCH_CV_FILE_NAME,
            cv_file_base64: dto.TCH_CV_FILE_BASE64,
            cv_file_mime_type: dto.TCH_CV_FILE_MIME_TYPE,
            dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
            portal_active: dto.TCH_PORTAL_ACTIVE != null ? Boolean(Number(dto.TCH_PORTAL_ACTIVE)) : false,
            portal_password: dto.TCH_PORTAL_PASSWORD || '',
            can_take_attendance: dto.TCH_CAN_TAKE_ATTENDANCE != null ? Boolean(Number(dto.TCH_CAN_TAKE_ATTENDANCE)) : false,
          };
        });
      }
    } catch (err) {
      console.error('Failed to load teachers from DB:', err);
    }
    return null;
  },

  createTeacher: async (teacher: Partial<Teacher>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_teacher,Insert',
        TCH_FULL_NAME: teacher.full_name || '',
        TCH_EMAIL: teacher.email || '',
        TCH_PHONE: teacher.phone || '',
        TCH_SPECIALIZATION: teacher.specialization || '',
        TCH_HOURLY_RATE: teacher.hourly_rate || 50,
        TCH_IS_ACTIVE: teacher.is_active !== undefined ? (teacher.is_active ? 1 : 0) : 1,
        TCH_BIO: teacher.bio || '',
        TCH_PORTAL_ACTIVE: teacher.portal_active ? 1 : 0,
        TCH_PORTAL_PASSWORD: teacher.portal_password || '',
        TCH_CAN_TAKE_ATTENDANCE: teacher.can_take_attendance ? 1 : 0
      }
    });
  },

  updateTeacher: async (id: number, teacher: Partial<Teacher>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_teacher,Update',
        TCH_IDF: id,
        TCH_FULL_NAME: teacher.full_name || '',
        TCH_EMAIL: teacher.email || '',
        TCH_PHONE: teacher.phone || '',
        TCH_SPECIALIZATION: teacher.specialization || '',
        TCH_HOURLY_RATE: teacher.hourly_rate || 50,
        TCH_IS_ACTIVE: teacher.is_active !== undefined ? (teacher.is_active ? 1 : 0) : 1,
        TCH_BIO: teacher.bio || '',
        TCH_PORTAL_ACTIVE: teacher.portal_active ? 1 : 0,
        TCH_PORTAL_PASSWORD: teacher.portal_password || '',
        TCH_CAN_TAKE_ATTENDANCE: teacher.can_take_attendance ? 1 : 0
      }
    });
  },

  deleteTeacher: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_teacher,Delete',
        TCH_IDF: id
      }
    });
  },

  // 3. Students
  getStudents: async (): Promise<Student[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_student,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: Number(dto.STD_IDF ?? dto.id) || 0,
          uid: dto.STD_UIDF || dto.UIDF || (dto.STD_IDF != null ? String(dto.STD_IDF) : ''),
          STD_UIDF: dto.STD_UIDF || dto.UIDF || (dto.STD_IDF != null ? String(dto.STD_IDF) : ''),
          code: dto.STD_CODE || (dto.STD_IDF != null ? `STD-${String(dto.STD_IDF).padStart(4, '0')}` : ''),
          STD_CODE: dto.STD_CODE || (dto.STD_IDF != null ? `STD-${String(dto.STD_IDF).padStart(4, '0')}` : ''),
          category: (dto.STD_CATEGORY as any) || 'adult',
          first_name: dto.STD_FIRST_NAME,
          last_name: dto.STD_LAST_NAME,
          email: dto.STD_EMAIL,
          phone: dto.STD_PHONE || '',
          date_of_birth: dto.STD_DATE_OF_BIRTH ? new Date(dto.STD_DATE_OF_BIRTH).toISOString().split('T')[0] : '',
          emergency_contact: dto.STD_EMERGENCY_CONTACT || '',
          financial_hold: Boolean(dto.STD_FINANCIAL_HOLD),
          hold_since: dto.STD_HOLD_SINCE ? new Date(dto.STD_HOLD_SINCE).toISOString().split('T')[0] : undefined,
          gender: dto.STD_GENDER,
          address: dto.STD_ADDRESS,
          profession: dto.STD_PROFESSION,
          school_level: dto.STD_SCHOOL_LEVEL,
          has_health_issues: dto.STD_HAS_HEALTH_ISSUES == null ? false : Boolean(dto.STD_HAS_HEALTH_ISSUES),
          health_conditions: dto.STD_HEALTH_CONDITIONS
            ? (typeof dto.STD_HEALTH_CONDITIONS === 'string'
              ? dto.STD_HEALTH_CONDITIONS.split(',').map((s: string) => s.trim()).filter(Boolean)
              : dto.STD_HEALTH_CONDITIONS)
            : [],
          health_other_detail: dto.STD_HEALTH_OTHER_DETAIL || '',
          has_allergies: dto.STD_HAS_ALLERGIES == null ? false : Boolean(dto.STD_HAS_ALLERGIES),
          allergy_details: dto.STD_ALLERGY_DETAILS || '',
          blood_group: dto.STD_BLOOD_GROUP || undefined,
          guardian_first_name: dto.STD_GUARDIAN_FIRST_NAME || '',
          guardian_last_name: dto.STD_GUARDIAN_LAST_NAME || '',
          guardian_relation: dto.STD_GUARDIAN_RELATION || undefined,
          guardian_phone: dto.STD_GUARDIAN_PHONE || '',
          guardian_email: dto.STD_GUARDIAN_EMAIL || '',
          authorized_pickup: dto.STD_AUTHORIZED_PICKUP || '',
          image_right: dto.STD_IMAGE_RIGHT == null ? true : Boolean(dto.STD_IMAGE_RIGHT),
          photo_base64: dto.STD_PHOTO_BASE64,
          photo_mime_type: dto.STD_PHOTO_MIME_TYPE,
          attached_file_name: dto.STD_FILE_NAME,
          attached_file_base64: dto.STD_FILE_BASE64,
          attached_file_mime_type: dto.STD_FILE_MIME_TYPE,
          registration_status: dto.STD_REGISTRATION_STATUS || 'Active',
          crs_idf: dto.CRS_IDF ? Number(dto.CRS_IDF) : undefined,
          time_preference: dto.STD_TIME_PREFERENCE || undefined,
          discount_id: dto.DSC_IDF != null && dto.DSC_IDF !== ''
            ? Number(dto.DSC_IDF)
            : (dto.discount_id != null && dto.discount_id !== '' ? Number(dto.discount_id) : undefined),
          discount_code: dto.DSC_CODE || dto.discount_code || undefined,
          discount_name: dto.DSC_NAME || dto.discount_name || undefined,
          loi_18_07_consent: dto.STD_LOI_18_07_CONSENT != null
            ? Boolean(dto.STD_LOI_18_07_CONSENT)
            : undefined,
          loi_18_07_consent_date: dto.STD_LOI_18_07_CONSENT_DATE
            ? new Date(dto.STD_LOI_18_07_CONSENT_DATE).toLocaleString('fr-DZ')
            : undefined,
          last_updt: dto.LAST_UPDT
            ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ')
            : undefined,
          submitted_date: toLocalDateStr(dto.STD_LOI_18_07_CONSENT_DATE ?? dto.LAST_UPDT),
          portal_active: dto.STD_PORTAL_ACTIVE != null ? Boolean(Number(dto.STD_PORTAL_ACTIVE)) : false,
          portal_password: dto.STD_PORTAL_PASSWORD || '',
        }));
      }
    } catch (err) {
      console.error('Failed to load students from DB:', err);
    }
    return null;
  },

  createStudent: async (student: Partial<Student>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_student,Insert',
        STD_CATEGORY: student.category || 'adult',
        STD_FIRST_NAME: student.first_name || '',
        STD_LAST_NAME: student.last_name || '',
        STD_GENDER: student.gender || null,
        STD_DATE_OF_BIRTH: student.date_of_birth || null,
        STD_ADDRESS: student.address || null,
        STD_EMAIL: student.email || null,
        STD_PHONE: student.phone || null,
        STD_EMERGENCY_CONTACT: student.emergency_contact || null,
        STD_FINANCIAL_HOLD: student.financial_hold ? 1 : 0,
        STD_HOLD_SINCE: student.hold_since || null,
        STD_PROFESSION: student.profession || null,
        STD_REGISTRATION_STATUS: student.registration_status || 'Active',
        STD_SCHOOL_LEVEL: student.school_level || null,
        STD_HAS_HEALTH_ISSUES: student.has_health_issues ?? null,
        STD_HEALTH_CONDITIONS: Array.isArray(student.health_conditions) && student.health_conditions.length
          ? student.health_conditions.join(',') : (typeof student.health_conditions === 'string' ? student.health_conditions : null),
        STD_HEALTH_OTHER_DETAIL: student.health_other_detail || null,
        STD_HAS_ALLERGIES: student.has_allergies ?? null,
        STD_ALLERGY_DETAILS: student.allergy_details || null,
        STD_BLOOD_GROUP: student.blood_group || null,
        STD_GUARDIAN_FIRST_NAME: student.guardian_first_name || null,
        STD_GUARDIAN_LAST_NAME: student.guardian_last_name || null,
        STD_GUARDIAN_RELATION: student.guardian_relation || null,
        STD_GUARDIAN_PHONE: student.guardian_phone || null,
        STD_GUARDIAN_EMAIL: student.guardian_email || null,
        STD_AUTHORIZED_PICKUP: student.authorized_pickup || null,
        STD_IMAGE_RIGHT: student.image_right ?? null,
        STD_PHOTO_BASE64: student.photo_base64 || null,
        STD_PHOTO_MIME_TYPE: student.photo_mime_type || null,
        STD_FILE_NAME: student.attached_file_name || null,
        STD_FILE_BASE64: student.attached_file_base64 || null,
        STD_FILE_MIME_TYPE: student.attached_file_mime_type || null,
        DSC_IDF: student.discount_id || null,
        CRS_IDF: student.crs_idf || null,
        STD_TIME_PREFERENCE: student.time_preference || null,
        STD_PORTAL_ACTIVE: student.portal_active ? 1 : 0,
        STD_PORTAL_PASSWORD: student.portal_password || '',
        ...(student.dos_idf ? { DOS_IDF: student.dos_idf } : {})
      }
    });
  },

  updateStudent: async (id: number, student: Partial<Student>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_student,Update',
        STD_IDF: id,
        STD_CATEGORY: student.category || 'adult',
        STD_FIRST_NAME: student.first_name || '',
        STD_LAST_NAME: student.last_name || '',
        STD_GENDER: student.gender || null,
        STD_DATE_OF_BIRTH: student.date_of_birth || null,
        STD_ADDRESS: student.address || null,
        STD_EMAIL: student.email || null,
        STD_PHONE: student.phone || null,
        STD_EMERGENCY_CONTACT: student.emergency_contact || null,
        STD_FINANCIAL_HOLD: student.financial_hold ? 1 : 0,
        STD_HOLD_SINCE: student.hold_since || null,
        STD_PROFESSION: student.profession || null,
        STD_REGISTRATION_STATUS: student.registration_status || 'Active',
        STD_SCHOOL_LEVEL: student.school_level || null,
        STD_HAS_HEALTH_ISSUES: student.has_health_issues ?? null,
        STD_HEALTH_CONDITIONS: Array.isArray(student.health_conditions) && student.health_conditions.length
          ? student.health_conditions.join(',') : (typeof student.health_conditions === 'string' ? student.health_conditions : null),
        STD_HEALTH_OTHER_DETAIL: student.health_other_detail || null,
        STD_HAS_ALLERGIES: student.has_allergies ?? null,
        STD_ALLERGY_DETAILS: student.allergy_details || null,
        STD_BLOOD_GROUP: student.blood_group || null,
        STD_GUARDIAN_FIRST_NAME: student.guardian_first_name || null,
        STD_GUARDIAN_LAST_NAME: student.guardian_last_name || null,
        STD_GUARDIAN_RELATION: student.guardian_relation || null,
        STD_GUARDIAN_PHONE: student.guardian_phone || null,
        STD_GUARDIAN_EMAIL: student.guardian_email || null,
        STD_AUTHORIZED_PICKUP: student.authorized_pickup || null,
        STD_IMAGE_RIGHT: student.image_right ?? null,
        STD_PHOTO_BASE64: student.photo_base64 || null,
        STD_PHOTO_MIME_TYPE: student.photo_mime_type || null,
        STD_FILE_NAME: student.attached_file_name || null,
        STD_FILE_BASE64: student.attached_file_base64 || null,
        STD_FILE_MIME_TYPE: student.attached_file_mime_type || null,
        DSC_IDF: student.discount_id || null,
        CRS_IDF: student.crs_idf || null,
        STD_TIME_PREFERENCE: student.time_preference || null,
        STD_PORTAL_ACTIVE: student.portal_active ? 1 : 0,
        STD_PORTAL_PASSWORD: student.portal_password || '',
        ...(student.dos_idf ? { DOS_IDF: student.dos_idf } : {})
      }
    });
  },

  deleteStudent: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_student,Delete',
        STD_IDF: id
      }
    });
  },

  updateStudentDiscount: async (studentId: number, discountId: number | null) => {
    // boubaker 31/08/2026 - Le contrôleur droppe les paramètres null.
    // Pour retirer une remise on envoie un flag REMOVE_DISCOUNT = true (non-null)
    // que HandleStudentAction côté serveur intercepte pour NULLifier DSC_IDF.
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: discountId != null
        ? {
            OKey: 'Blobtic_Academy,academy_student,Update',
            STD_IDF: studentId,
            DSC_IDF: discountId,
          }
        : {
            OKey: 'Blobtic_Academy,academy_student,Update',
            STD_IDF: studentId,
            REMOVE_DISCOUNT: true,
          }
    });
  },

  // 4. Course Templates
  getCourses: async (): Promise<CourseTemplate[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_course,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id:          dto.CRS_IDF,
          crs_uidf:    dto.CRS_UIDF  || undefined,
          title:       dto.CRS_TITLE       || '',
          total_hours: Number(dto.CRS_TOTAL_HOURS) || 0,
          price:       Number(dto.CRS_PRICE)       || 0,
          show_price:  dto.CRS_SHOW_PRICE != null ? Boolean(Number(dto.CRS_SHOW_PRICE)) : true,
          description: dto.CRS_DESCRIPTION || '',
          start_date:  dto.CRS_START_DATE  ? new Date(dto.CRS_START_DATE).toISOString().split('T')[0] : undefined,
          end_date:    dto.CRS_END_DATE    ? new Date(dto.CRS_END_DATE).toISOString().split('T')[0]   : undefined,
          age_band:    dto.CRS_AGE_BAND    || undefined,
          age_min:     dto.CRS_AGE_MIN  != null ? Number(dto.CRS_AGE_MIN)  : undefined,
          age_max:     dto.CRS_AGE_MAX  != null ? Number(dto.CRS_AGE_MAX)  : undefined,
          dos_idf:     dto.DOS_IDF      ? Number(dto.DOS_IDF) : undefined,
          dos_code:    dto.DOS_CODE     || undefined,
          dos_lib:     dto.DOS_LIB      || undefined,
          user_idf:    dto.USER_IDF     ? Number(dto.USER_IDF) : undefined,
          user_lib:    dto.USER_LIB     || undefined,
          last_updt:   dto.LAST_UPDT   ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load courses from DB:', err);
    }
    return null;
  },

  createCourse: async (course: Partial<CourseTemplate>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_course,Insert',
        CRS_TITLE:       course.title       || '',
        CRS_TOTAL_HOURS: course.total_hours ?? 80,
        CRS_PRICE:       course.price       ?? 0,
        CRS_SHOW_PRICE:  course.show_price  !== false ? 1 : 0,
        CRS_DESCRIPTION: course.description || '',
        ...(course.start_date ? { CRS_START_DATE: course.start_date } : {}),
        ...(course.end_date   ? { CRS_END_DATE:   course.end_date   } : {}),
        ...(course.age_band   ? { CRS_AGE_BAND:   course.age_band   } : {}),
        ...(course.age_min  != null ? { CRS_AGE_MIN: course.age_min } : {}),
        ...(course.age_max  != null ? { CRS_AGE_MAX: course.age_max } : {}),
        ...(course.dos_idf  ? { DOS_IDF: course.dos_idf } : {})
      }
    });
  },

  updateCourse: async (id: number, course: Partial<CourseTemplate>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_course,Update',
        CRS_IDF:         id,
        CRS_TITLE:       course.title       || '',
        CRS_TOTAL_HOURS: course.total_hours ?? 80,
        CRS_PRICE:       course.price       ?? 0,
        CRS_SHOW_PRICE:  course.show_price  !== false ? 1 : 0,
        CRS_DESCRIPTION: course.description || '',
        ...(course.start_date ? { CRS_START_DATE: course.start_date } : {}),
        ...(course.end_date   ? { CRS_END_DATE:   course.end_date   } : {}),
        ...(course.age_band   ? { CRS_AGE_BAND:   course.age_band   } : {}),
        ...(course.age_min  != null ? { CRS_AGE_MIN: course.age_min } : {}),
        ...(course.age_max  != null ? { CRS_AGE_MAX: course.age_max } : {}),
        ...(course.dos_idf  ? { DOS_IDF: course.dos_idf } : {})
      }
    });
  },

  deleteCourse: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_course,Delete',
        CRS_IDF: id
      }
    });
  },

  // 5. Groups
  getGroups: async (): Promise<Group[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_group,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.GRP_IDF,
          name: dto.GRP_NAME,
          course_id: dto.COURSE_IDF || 1,
          teacher_id: dto.TCH_IDF || 1,
          max_capacity: dto.GRP_MAX_CAPACITY || 20,
          start_date: dto.GRP_START_DATE ? new Date(dto.GRP_START_DATE).toISOString().split('T')[0] : '',
          expected_end_date: dto.GRP_EXPECTED_END_DATE ? new Date(dto.GRP_EXPECTED_END_DATE).toISOString().split('T')[0] : '',
          status: dto.GRP_STATUS || 'Upcoming',
          dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
          dos_code: dto.DOS_CODE || undefined,
          dos_lib: dto.DOS_LIB || undefined,
          user_lib: dto.USER_LIB || undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load groups from DB:', err);
    }
    return null;
  },

  createGroup: async (group: Partial<Group>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_group,Insert',
        GRP_NAME: group.name || '',
        COURSE_IDF: group.course_id || 1,
        TCH_IDF: group.teacher_id || 1,
        GRP_MAX_CAPACITY: group.max_capacity || 20,
        GRP_START_DATE: group.start_date || '',
        GRP_EXPECTED_END_DATE: group.expected_end_date || '',
        GRP_STATUS: group.status || 'Upcoming'
      }
    });
  },

  updateGroup: async (id: number, group: Partial<Group>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_group,Update',
        GRP_IDF: id,
        GRP_NAME: group.name || '',
        COURSE_IDF: group.course_id || 1,
        TCH_IDF: group.teacher_id || 1,
        GRP_MAX_CAPACITY: group.max_capacity || 20,
        GRP_START_DATE: group.start_date || '',
        GRP_EXPECTED_END_DATE: group.expected_end_date || '',
        GRP_STATUS: group.status || 'Upcoming'
      }
    });
  },

  deleteGroup: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_group,Delete',
        GRP_IDF: id
      }
    });
  },

  // 6. GroupStudents
  getGroupStudents: async (useCache: boolean = true): Promise<GroupStudent[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_group_student,Select' },
        useCache
      }, useCache);
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => mapGroupStudent(dto));
      }
    } catch (err) {
      console.error('Failed to load group students from DB:', err);
    }
    return null;
  },

  enrollStudent: async (groupId: number, studentId: number, discountId?: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_group_student,Insert',
        GRP_IDF: groupId,
        STD_IDF: studentId,
        GST_STATUS: 'Enrolled',
        ...(discountId && discountId > 0 ? { DSC_IDF: discountId } : {})
      },
      useCache: false
    }, false);
  },

  unenrollStudent: async (gstId: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_group_student,Delete',
        GST_IDF: gstId
      },
      useCache: false
    }, false);
  },

  // 6. Sessions
  getSessions: async (): Promise<Session[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_session,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => mapSession(dto));
      }
    } catch (err) {
      console.error('Failed to load sessions from DB:', err);
    }
    return null;
  },

  // boubaker 22/08/2026 - chargement planning filtré (plage de dates + salle/enseignant/groupe)
  getSessionsRange: async (
    dateFrom: string,
    dateTo: string,
    filters?: { room_id?: number | 'all'; teacher_id?: number | 'all'; group_id?: number | 'all' }
  ): Promise<Session[] | null> => {
    try {
      const action: { OKey: string; [key: string]: string | number } = {
        OKey: 'Blobtic_Academy,academy_session,Select',
        SSN_DATE_FROM: `${dateFrom}T00:00:00`,
        SSN_DATE_TO: `${dateTo}T23:59:59`
      };
      if (filters?.room_id && filters.room_id !== 'all') action.ARM_IDF = filters.room_id;
      if (filters?.teacher_id && filters.teacher_id !== 'all') action.TCH_IDF = filters.teacher_id;
      if (filters?.group_id && filters.group_id !== 'all') action.GRP_IDF = filters.group_id;

      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: action
      }, false);
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => mapSession(dto));
      }
    } catch (err) {
      console.error('Failed to load sessions range from DB:', err);
    }
    return null;
  },

  createSession: async (session: Partial<Session>) => {
    // boubaker 22/08/2026 - datetime complets : start_hour ISO sinon date+start_time
    const startIso = session.start_hour || combineLocalDateTime(session.date, session.start_time);
    const endIso = session.end_hour || combineLocalDateTime(session.date, session.end_time);
    const dur = session.duration && session.duration > 0
      ? session.duration
      : durationMinutes(startIso, endIso, 120);

    // Duplication côté serveur : SSN_REPEAT_WEEKS = N → N lignes concrètes en base
    const repeatWeeks = session.is_recurring && session.repeat_weeks && session.repeat_weeks > 1
      ? session.repeat_weeks
      : 1;

    const action: { OKey: string; [key: string]: string | number } = {
      OKey: 'Blobtic_Academy,academy_session,Insert',
      GRP_IDF: session.group_id || 1,
      ARM_IDF: session.room_id || 1,
      TCH_IDF: session.teacher_id || 1,
      SSN_START_HOUR: startIso,
      SSN_END_HOUR: endIso,
      SSN_DURATION: dur,
      SSN_REPEAT_WEEKS: repeatWeeks,
      SSN_ATTENDANCE_TAKEN: session.attendance_taken ? 1 : 0
    };

    if (session.parent_session_id) {
      action.SSN_PARENT_SESSION_IDF = session.parent_session_id;
    }

    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: action
    });
  },

  updateSession: async (id: number, session: Partial<Session>) => {
    const startIso = session.start_hour || combineLocalDateTime(session.date, session.start_time);
    const endIso = session.end_hour || combineLocalDateTime(session.date, session.end_time);
    const dur = session.duration && session.duration > 0
      ? session.duration
      : durationMinutes(startIso, endIso, 120);

    // boubaker 22/08/2026 - Update partiel : seuls les champs envoyés sont mis à jour côté SQL
    const action: { OKey: string; [key: string]: string | number } = {
      OKey: 'Blobtic_Academy,academy_session,Update',
      SSN_IDF: id
    };
    if (session.group_id != null) action.GRP_IDF = session.group_id;
    if (session.room_id != null) action.ARM_IDF = session.room_id;
    if (session.teacher_id != null) action.TCH_IDF = session.teacher_id;
    if (startIso) action.SSN_START_HOUR = startIso;
    if (endIso) action.SSN_END_HOUR = endIso;
    action.SSN_DURATION = dur;
    if (session.attendance_taken != null) action.SSN_ATTENDANCE_TAKEN = session.attendance_taken ? 1 : 0;
    if (session.status != null) action.SSN_STATUS = session.status;
    if (session.status_reason != null) action.SSN_STATUS_REASON = session.status_reason;
    if (session.postponed_date != null) action.SSN_POSTPONED_DATE = session.postponed_date;
    if (session.postponed_time != null) action.SSN_POSTPONED_TIME = session.postponed_time;
    if (session.teacher_absent != null) action.SSN_TEACHER_ABSENT = session.teacher_absent ? 1 : 0;

    if (session.parent_session_id) {
      action.SSN_PARENT_SESSION_IDF = session.parent_session_id;
    }

    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: action
    });
  },

  deleteSession: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_session,Delete',
        SSN_IDF: id
      }
    });
  },

  // 6b. Teacher Absence Requests
  getTeacherAbsenceRequests: async (): Promise<TeacherAbsenceRequest[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_teacher_absence_request,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => mapTeacherAbsenceRequest(dto));
      }
    } catch (err) {
      console.error('Failed to load teacher absence requests from DB:', err);
    }
    return null;
  },

  updateTeacherAbsenceRequest: async (id: number, data: Partial<TeacherAbsenceRequest> & { decision_notes?: string }) => {
    const action: { OKey: string; [key: string]: string | number | undefined } = {
      OKey: 'Blobtic_Academy,academy_teacher_absence_request,Update',
      TAR_IDF: id
    };
    if (data.status != null) {
      const st = data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase();
      action.TAR_STATUS = st;
    }
    if (data.admin_comment != null || data.decision_notes != null) {
      action.TAR_DECISION_NOTES = data.admin_comment ?? data.decision_notes;
    }
    if (data.reason != null) action.TAR_REASON = data.reason;

    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: action
    });
  },

  // 7. Attendances
  getAttendances: async (): Promise<Attendance[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_attendance,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map(mapAttendance);
      }
    } catch (err) {
      console.error('Failed to load attendances from DB:', err);
    }
    return null;
  },

  createAttendance: async (attendance: Partial<Attendance>) => {
    const action: any = {
      OKey: 'Blobtic_Academy,academy_attendance,Insert',
      SSN_IDF: attendance.session_id || 1,
      STD_IDF: attendance.student_id || 1,
      ATD_STATUS: attendance.status || 'Present',
      ATD_CHECK_IN_TIME: attendance.check_in_time || ''
    };
    if (attendance.justified !== undefined) action.ATD_JUSTIFIED = attendance.justified ? 1 : 0;
    if (attendance.justification_reason !== undefined) action.ATD_JUSTIFICATION_REASON = attendance.justification_reason;
    if (attendance.justification_doc_name !== undefined) action.ATD_JUSTIFICATION_DOC_NAME = attendance.justification_doc_name;
    if (attendance.justification_doc_base64 !== undefined) action.ATD_JUSTIFICATION_DOC_BASE64 = attendance.justification_doc_base64;
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: action
    });
  },

  updateAttendance: async (id: number, attendance: Partial<Attendance>) => {
    const action: any = {
      OKey: 'Blobtic_Academy,academy_attendance,Update',
      ATD_IDF: id
    };
    if (attendance.status !== undefined) action.ATD_STATUS = attendance.status;
    if (attendance.check_in_time !== undefined) action.ATD_CHECK_IN_TIME = attendance.check_in_time;
    if (attendance.justified !== undefined) action.ATD_JUSTIFIED = attendance.justified ? 1 : 0;
    if (attendance.justification_reason !== undefined) action.ATD_JUSTIFICATION_REASON = attendance.justification_reason;
    if (attendance.justification_doc_name !== undefined) action.ATD_JUSTIFICATION_DOC_NAME = attendance.justification_doc_name;
    if (attendance.justification_doc_base64 !== undefined) action.ATD_JUSTIFICATION_DOC_BASE64 = attendance.justification_doc_base64;
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: action
    });
  },

  // 8. Invoices
  getInvoices: async (useCache: boolean = true): Promise<Invoice[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_invoice,Select' },
        useCache
      }, useCache);
      if (res.success && Array.isArray(res.data)) {
        return res.data.map(mapInvoice);
      }
    } catch (err) {
      console.error('Failed to load invoices from DB:', err);
    }
    return null;
  },

  createInvoice: async (invoice: Partial<Invoice>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_invoice,Insert',
        STD_IDF: invoice.student_id || 1,
        GRP_IDF: invoice.group_id || 1,
        INV_NUMBER: invoice.invoice_number || '',
        INV_TOTAL_AMOUNT: invoice.total_amount || 0,
        INV_AMOUNT_PAID: invoice.amount_paid || 0,
        INV_ISSUE_DATE: invoice.issue_date || '',
        INV_DUE_DATE: invoice.due_date || '',
        INV_STATUS: invoice.status || 'Unpaid',
        ...(invoice.discount_id ? { DSC_IDF: invoice.discount_id } : {}),
        ...(invoice.discount_code ? { INV_DISCOUNT_CODE: invoice.discount_code } : {}),
        ...(invoice.discount_percent != null ? { INV_DISCOUNT_PERCENT: invoice.discount_percent } : {}),
        ...(invoice.discount_amount != null ? { INV_DISCOUNT_AMOUNT: invoice.discount_amount } : {}),
        ...(invoice.original_amount != null ? { INV_ORIGINAL_AMOUNT: invoice.original_amount } : {}),
        ...(invoice.discount_reason ? { INV_DISCOUNT_REASON: invoice.discount_reason } : {}),
        ...(invoice.discount_approved != null ? { INV_DISCOUNT_APPROVED: invoice.discount_approved ? 1 : 0 } : {}),
        ...(invoice.discount_approved_by ? { INV_DISCOUNT_APPROVED_BY: invoice.discount_approved_by } : {})
      }
    });
  },

  updateInvoice: async (id: number, invoice: Partial<Invoice>) => {
    const action: { OKey: string; [key: string]: string | number } = {
      OKey: 'Blobtic_Academy,academy_invoice,Update',
      INV_IDF: id,
      INV_TOTAL_AMOUNT: invoice.total_amount ?? 0,
      INV_AMOUNT_PAID: invoice.amount_paid ?? 0,
      INV_STATUS: invoice.status || 'Unpaid'
    };

    if (invoice.issue_date) action.INV_ISSUE_DATE = invoice.issue_date;
    if (invoice.due_date) action.INV_DUE_DATE = invoice.due_date;
    if (invoice.discount_id != null) action.DSC_IDF = invoice.discount_id;
    if (invoice.discount_code) action.INV_DISCOUNT_CODE = invoice.discount_code;
    if (invoice.discount_percent != null) action.INV_DISCOUNT_PERCENT = invoice.discount_percent;
    if (invoice.discount_amount != null) action.INV_DISCOUNT_AMOUNT = invoice.discount_amount;
    if (invoice.original_amount != null) action.INV_ORIGINAL_AMOUNT = invoice.original_amount;
    if (invoice.discount_reason != null) action.INV_DISCOUNT_REASON = invoice.discount_reason;
    if (invoice.discount_approved != null) action.INV_DISCOUNT_APPROVED = invoice.discount_approved ? 1 : 0;
    if (invoice.discount_approved_by != null) action.INV_DISCOUNT_APPROVED_BY = invoice.discount_approved_by;

    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: action
    });
  },

  // 9. Payments
  getPayments: async (useCache: boolean = true): Promise<Payment[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_payment,Select' },
        useCache
      }, useCache);
      if (res.success && Array.isArray(res.data)) {
        return res.data.map(mapPayment);
      }
    } catch (err) {
      console.error('Failed to load payments from DB:', err);
    }
    return null;
  },

  createPayment: async (payment: Partial<Payment>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_payment,Insert',
        INV_IDF: payment.invoice_id || 1,
        STD_IDF: payment.student_id || 1,
        PMT_AMOUNT: payment.amount || 0,
        PMT_DATE: payment.payment_date || '',
        PMT_METHOD: payment.method || 'Cash',
        PMT_RECEIPT_NUMBER: payment.receipt_number || ''
      }
    });
  },

  recordStudentPayment: async (studentId: number, amount: number, method: PaymentMethod) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_payment,Record',
        STD_IDF: studentId,
        PMT_AMOUNT: amount,
        PMT_METHOD: method || 'Cash'
      },
      useCache: false
    }, false);
  },

  deletePayment: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_payment,Delete',
        PMT_IDF: id
      }
    });
  },

  // boubaker 23/08/2026 - suppression facture (contrôles serveur : paiements existants / étudiant rattaché au groupe)
  deleteInvoice: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_invoice,Delete',
        INV_IDF: id
      }
    });
  },

  // 10. Quizzes
  getQuizzes: async (): Promise<Quiz[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_quiz,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.QIZ_IDF,
          code: dto.QIZ_CODE || '',
          title: dto.QIZ_TITLE || '',
          description: dto.QIZ_DESCRIPTION || '',
          duration_minutes: Number(dto.QIZ_DURATION_MINUTES) || 5,
          expiry_days: Number(dto.QIZ_EXPIRY_DAYS) || 7,
          whatsapp: dto.QIZ_WHATSAPP || undefined,
          register_link: dto.QIZ_REGISTER_LINK || undefined,
          display: (dto.QIZ_DISPLAY === 'assistant' ? 'assistant' : 'list') as Quiz['display'],
          auto_next: Boolean(dto.QIZ_AUTO_NEXT),
          QIZ_AUTO_NEXT: Boolean(dto.QIZ_AUTO_NEXT),
          max_attempts_link: Number(dto.QIZ_MAX_ATTEMPTS_LINK) || 50,
          max_attempts_user: Number(dto.QIZ_MAX_ATTEMPTS_USER) || 3,
          QIZ_MAX_ATTEMPTS_LINK: Number(dto.QIZ_MAX_ATTEMPTS_LINK) || 50,
          QIZ_MAX_ATTEMPTS_USER: Number(dto.QIZ_MAX_ATTEMPTS_USER) || 3,
          status: (dto.QIZ_STATUS || 'Draft') as Quiz['status'],
          start_date: dto.QIZ_START_DATE ? new Date(dto.QIZ_START_DATE).toISOString().split('T')[0] : undefined,
          end_date: dto.QIZ_END_DATE ? new Date(dto.QIZ_END_DATE).toISOString().split('T')[0] : undefined,
          dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
          dos_code: dto.DOS_CODE || undefined,
          dos_lib: dto.DOS_LIB || undefined,
          user_lib: dto.USER_LIB || undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load quizzes from DB:', err);
    }
    return null;
  },

  createQuiz: async (quiz: Partial<Quiz>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz,Insert',
        QIZ_CODE: quiz.code || '',
        QIZ_TITLE: quiz.title || '',
        QIZ_DESCRIPTION: quiz.description || '',
        QIZ_DURATION_MINUTES: quiz.duration_minutes ?? 5,
        QIZ_EXPIRY_DAYS: quiz.expiry_days ?? 7,
        QIZ_WHATSAPP: quiz.whatsapp || '',
        QIZ_REGISTER_LINK: quiz.register_link || '',
        QIZ_DISPLAY: quiz.display || 'list',
        QIZ_AUTO_NEXT: quiz.auto_next ?? quiz.QIZ_AUTO_NEXT ?? false,
        QIZ_MAX_ATTEMPTS_LINK: quiz.max_attempts_link ?? quiz.QIZ_MAX_ATTEMPTS_LINK ?? 50,
        QIZ_MAX_ATTEMPTS_USER: quiz.max_attempts_user ?? quiz.QIZ_MAX_ATTEMPTS_USER ?? 3,
        QIZ_STATUS: quiz.status || 'Draft',
        ...(quiz.start_date ? { QIZ_START_DATE: quiz.start_date } : {}),
        ...(quiz.end_date ? { QIZ_END_DATE: quiz.end_date } : {}),
        ...(quiz.dos_idf ? { DOS_IDF: quiz.dos_idf } : {})
      }
    });
  },

  updateQuiz: async (id: number, quiz: Partial<Quiz>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz,Update',
        QIZ_IDF: id,
        QIZ_CODE: quiz.code || '',
        QIZ_TITLE: quiz.title || '',
        QIZ_DESCRIPTION: quiz.description || '',
        QIZ_DURATION_MINUTES: quiz.duration_minutes ?? 5,
        QIZ_EXPIRY_DAYS: quiz.expiry_days ?? 7,
        QIZ_WHATSAPP: quiz.whatsapp || '',
        QIZ_REGISTER_LINK: quiz.register_link || '',
        QIZ_DISPLAY: quiz.display || 'list',
        QIZ_AUTO_NEXT: quiz.auto_next ?? quiz.QIZ_AUTO_NEXT ?? false,
        QIZ_MAX_ATTEMPTS_LINK: quiz.max_attempts_link ?? quiz.QIZ_MAX_ATTEMPTS_LINK ?? 50,
        QIZ_MAX_ATTEMPTS_USER: quiz.max_attempts_user ?? quiz.QIZ_MAX_ATTEMPTS_USER ?? 3,
        QIZ_STATUS: quiz.status || 'Draft',
        ...(quiz.start_date ? { QIZ_START_DATE: quiz.start_date } : {}),
        ...(quiz.end_date ? { QIZ_END_DATE: quiz.end_date } : {}),
        ...(quiz.dos_idf ? { DOS_IDF: quiz.dos_idf } : {})
      }
    });
  },

  deleteQuiz: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz,Delete',
        QIZ_IDF: id
      }
    });
  },

  // 11. Quiz Questions
  getQuizQuestions: async (quizId?: number): Promise<QuizQuestion[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: {
          OKey: 'Blobtic_Academy,academy_quiz_question,Select',
          ...(quizId ? { QIZ_IDF: quizId } : {})
        }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.QST_IDF,
          quiz_id: dto.QIZ_IDF,
          text: dto.QST_TEXT || '',
          type: (dto.QST_TYPE || 'single') as QuizQuestionType,
          options: parseQuestionOptions(dto.QST_OPTIONS),
          order: Number(dto.QST_ORDER) || 1,
          points: Number(dto.QST_POINTS) || 1,
        }));
      }
    } catch (err) {
      console.error('Failed to load quiz questions from DB:', err);
    }
    return null;
  },

  createQuizQuestion: async (question: Partial<QuizQuestion>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz_question,Insert',
        QIZ_IDF: question.quiz_id || 0,
        QST_TEXT: question.text || '',
        QST_TYPE: question.type || 'single',
        QST_OPTIONS: JSON.stringify((question.options || []).map(o => ({
          key: o.key,
          label: o.label,
          isCorrect: !!o.isCorrect
        }))),
        QST_ORDER: question.order ?? 1,
        QST_POINTS: question.points ?? 1
      }
    });
  },

  updateQuizQuestion: async (id: number, question: Partial<QuizQuestion>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz_question,Update',
        QST_IDF: id,
        QIZ_IDF: question.quiz_id || 0,
        QST_TEXT: question.text || '',
        QST_TYPE: question.type || 'single',
        QST_OPTIONS: JSON.stringify((question.options || []).map(o => ({
          key: o.key,
          label: o.label,
          isCorrect: !!o.isCorrect
        }))),
        QST_ORDER: question.order ?? 1,
        QST_POINTS: question.points ?? 1
      }
    });
  },

  deleteQuizQuestion: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz_question,Delete',
        QST_IDF: id
      }
    });
  },

  // 12. Quiz Levels
  getQuizLevels: async (quizId?: number): Promise<QuizLevel[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: {
          OKey: 'Blobtic_Academy,academy_quiz_level,Select',
          ...(quizId ? { QIZ_IDF: quizId } : {})
        }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.LVL_IDF,
          quiz_id: dto.QIZ_IDF,
          code: dto.LVL_CODE || '',
          title: dto.LVL_TITLE || '',
          min_score: Number(dto.LVL_MIN_SCORE) || 0,
          max_score: Number(dto.LVL_MAX_SCORE) || 0,
          message: dto.LVL_MESSAGE || '',
        }));
      }
    } catch (err) {
      console.error('Failed to load quiz levels from DB:', err);
    }
    return null;
  },

  createQuizLevel: async (level: Partial<QuizLevel>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz_level,Insert',
        QIZ_IDF: level.quiz_id || 0,
        LVL_CODE: level.code || '',
        LVL_TITLE: level.title || '',
        LVL_MIN_SCORE: level.min_score ?? 0,
        LVL_MAX_SCORE: level.max_score ?? 0,
        LVL_MESSAGE: level.message || ''
      }
    });
  },

  updateQuizLevel: async (id: number, level: Partial<QuizLevel>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz_level,Update',
        LVL_IDF: id,
        QIZ_IDF: level.quiz_id || 0,
        LVL_CODE: level.code || '',
        LVL_TITLE: level.title || '',
        LVL_MIN_SCORE: level.min_score ?? 0,
        LVL_MAX_SCORE: level.max_score ?? 0,
        LVL_MESSAGE: level.message || ''
      }
    });
  },

  deleteQuizLevel: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_quiz_level,Delete',
        LVL_IDF: id
      }
    });
  },

  // 13. Quiz Attempts
  getQuizAttempts: async (quizId?: number): Promise<QuizAttempt[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: {
          OKey: 'Blobtic_Academy,academy_quiz_attempt,Select',
          ...(quizId ? { QIZ_IDF: quizId } : {})
        }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.ATT_IDF,
          quiz_id: dto.QIZ_IDF,
          quiz_title: dto.QIZ_TITLE || undefined,
          full_name: dto.ATT_FULL_NAME || '',
          phone: dto.ATT_PHONE || '',
          email: dto.ATT_EMAIL || '',
          age_band: dto.ATT_AGE_BAND || '',
          score: Number(dto.ATT_SCORE) || 0,
          total: Number(dto.ATT_TOTAL) || 0,
          level_code: dto.ATT_LEVEL_CODE || '',
          answers: dto.ATT_ANSWERS || '',
          ref_code: dto.ATT_REF_CODE || '',
          duration_seconds: dto.ATT_DURATION_SECONDS ? Number(dto.ATT_DURATION_SECONDS) : undefined,
          ip_address: dto.ATT_IP_ADDRESS || '',
          dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load quiz attempts from DB:', err);
    }
    return null;
  },

  // 14. Academy Parameters
  getAcademyParameters: async (dosIdf?: number): Promise<AcademyParameter[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: {
          OKey: 'Blobtic_Academy,academy_parametre,Select',
          ...(dosIdf ? { DOS_IDF: dosIdf } : {})
        }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.PRM_IDF,
          prm_uidf: dto.PRM_UIDF,
          dos_idf: Number(dto.DOS_IDF || 1),
          dos_code: dto.DOS_CODE || '',
          dos_lib: dto.DOS_LIB || '',
          code: dto.PRM_CODE || '',
          value: dto.PRM_VALUE || '',
          lib: dto.PRM_LIB || '',
          type: dto.PRM_TYPE || 'S',
          user_idf: dto.USER_IDF ? Number(dto.USER_IDF) : undefined,
          user_lib: dto.USER_LIB || undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load academy parameters from DB:', err);
    }
    return null;
  },

  saveAcademyParameter: async (param: Partial<AcademyParameter>) => {
    const isUpdate = Boolean(param.id);
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: `Blobtic_Academy,academy_parametre,${isUpdate ? 'Update' : 'Insert'}`,
        ...(isUpdate ? { PRM_IDF: param.id } : {}),
        DOS_IDF: param.dos_idf || 1,
        PRM_CODE: param.code,
        PRM_VALUE: param.value,
        PRM_LIB: param.lib || '',
        PRM_TYPE: param.type || 'S'
      }
    });
  },

  deleteAcademyParameter: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_parametre,Delete',
        PRM_IDF: id
      }
    });
  },

  // 15. Planning Controls
  getPlanningControls: async (): Promise<PlanningControl[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_planning_control,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.CTL_IDF,
          ctl_uidf: dto.CTL_UIDF,
          dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
          code: dto.CTL_CODE || '',
          lib: dto.CTL_LIB || '',
          is_active: Boolean(dto.CTL_IS_ACTIVE),
          is_blocking: Boolean(dto.CTL_IS_BLOCKING),
          description: dto.CTL_DESCRIPTION || '',
          user_lib: dto.USER_LIB || undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load planning controls from DB:', err);
    }
    return null;
  },

  savePlanningControl: async (ctrl: Partial<PlanningControl>) => {
    const isUpdate = Boolean(ctrl.id);
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: `Blobtic_Academy,academy_planning_control,${isUpdate ? 'Update' : 'Insert'}`,
        ...(isUpdate ? { CTL_IDF: ctrl.id } : {}),
        CTL_CODE: ctrl.code || '',
        CTL_LIB: ctrl.lib || '',
        CTL_IS_ACTIVE: ctrl.is_active ? 1 : 0,
        CTL_IS_BLOCKING: ctrl.is_blocking ? 1 : 0,
        CTL_DESCRIPTION: ctrl.description || ''
      }
    });
  },

  deletePlanningControl: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_planning_control,Delete',
        CTL_IDF: id
      }
    });
  },

  // 16. Discounts
  getDiscounts: async (): Promise<AcademyDiscount[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_discount,Select' }
      });
      if (res.success && Array.isArray(res.data)) {
        return res.data.map((dto: any) => ({
          id: dto.DSC_IDF,
          dsc_uidf: dto.DSC_UIDF,
          dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
          code: dto.DSC_CODE || '',
          name: dto.DSC_NAME || '',
          percentage: Number(dto.DSC_PERCENTAGE) || 0,
          requires_validation: Boolean(dto.DSC_REQUIRES_VALIDATION),
          validator_role: dto.DSC_VALIDATOR_ROLE || 'Admin',
          is_auto_applied: Boolean(dto.DSC_IS_AUTO_APPLIED),
          min_courses: dto.DSC_MIN_COURSES != null ? Number(dto.DSC_MIN_COURSES) : 1,
          description: dto.DSC_DESCRIPTION || '',
          is_active: Boolean(dto.DSC_IS_ACTIVE),
          user_lib: dto.USER_LIB || undefined,
          last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
        }));
      }
    } catch (err) {
      console.error('Failed to load discounts from DB:', err);
    }
    return null;
  },

  createDiscount: async (discount: Partial<AcademyDiscount>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount,Insert',
        DSC_CODE: discount.code || '',
        DSC_NAME: discount.name || '',
        DSC_PERCENTAGE: discount.percentage ?? 0,
        DSC_REQUIRES_VALIDATION: discount.requires_validation ? 1 : 0,
        DSC_VALIDATOR_ROLE: discount.validator_role || 'Admin',
        DSC_IS_AUTO_APPLIED: discount.is_auto_applied ? 1 : 0,
        DSC_MIN_COURSES: discount.min_courses ?? 1,
        DSC_DESCRIPTION: discount.description || '',
        DSC_IS_ACTIVE: discount.is_active !== false ? 1 : 0
      }
    });
  },

  updateDiscount: async (id: number, discount: Partial<AcademyDiscount>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount,Update',
        DSC_IDF: id,
        DSC_CODE: discount.code || '',
        DSC_NAME: discount.name || '',
        DSC_PERCENTAGE: discount.percentage ?? 0,
        DSC_REQUIRES_VALIDATION: discount.requires_validation ? 1 : 0,
        DSC_VALIDATOR_ROLE: discount.validator_role || 'Admin',
        DSC_IS_AUTO_APPLIED: discount.is_auto_applied ? 1 : 0,
        DSC_MIN_COURSES: discount.min_courses ?? 1,
        DSC_DESCRIPTION: discount.description || '',
        DSC_IS_ACTIVE: discount.is_active !== false ? 1 : 0
      }
    });
  },

  deleteDiscount: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount,Delete',
        DSC_IDF: id
      }
    });
  },

  // boubaker 23/08/2026 - Impressions : chargements serveur autonomes via la DLL Blobtic_Print
  getPrintPlanning: async (
    dateFrom: string,
    dateTo: string,
    filters?: { room_id?: number | 'all'; teacher_id?: number | 'all'; group_id?: number | 'all' }
  ): Promise<any[] | null> => {
    try {
      const action: { OKey: string; [key: string]: string | number } = {
        OKey: 'Blobtic_Print,academy_print,load_print_planning',
        DATE_FROM: dateFrom,
        DATE_TO: dateTo
      };
      if (filters?.room_id && filters.room_id !== 'all') action.ARM_IDF = filters.room_id;
      if (filters?.teacher_id && filters.teacher_id !== 'all') action.TCH_IDF = filters.teacher_id;
      if (filters?.group_id && filters.group_id !== 'all') action.GRP_IDF = filters.group_id;

      const res = await blobticService.executeZoomRequest({ ZoomKey: 'Blobtic_Print', Action: action }, false);
      // boubaker 23/08/2026 - le contrôleur encapsule les payloads objet dans une liste à un élément
      const data = (Array.isArray(res.data) ? (res.data as any)[0] : res.data) as any;
      if (res.success && data && Array.isArray(data.sessions)) {
        return data.sessions;
      }
    } catch (err) {
      console.error('Failed to load print planning:', err);
    }
    return null;
  },

  getPrintInvoices: async (
    dateFrom: string,
    dateTo: string,
    status?: 'all' | 'Paid' | 'Unpaid' | 'Partial'
  ): Promise<any[] | null> => {
    try {
      const action: { OKey: string; [key: string]: string | number } = {
        OKey: 'Blobtic_Print,academy_print,load_print_invoices',
        DATE_FROM: dateFrom,
        DATE_TO: dateTo
      };
      if (status && status !== 'all') action.INV_STATUS = status;

      const res = await blobticService.executeZoomRequest({ ZoomKey: 'Blobtic_Print', Action: action }, false);
      const data = (Array.isArray(res.data) ? (res.data as any)[0] : res.data) as any;
      if (res.success && data && Array.isArray(data.invoices)) {
        return data.invoices;
      }
    } catch (err) {
      console.error('Failed to load print invoices:', err);
    }
    return null;
  },

  getPrintReceipts: async (
    dateFrom: string,
    dateTo: string,
    method?: 'all' | 'Cash' | 'Card' | 'Transfer'
  ): Promise<{ payments: any[]; total: number } | null> => {
    try {
      const action: { OKey: string; [key: string]: string | number } = {
        OKey: 'Blobtic_Print,academy_print,load_print_receipts',
        DATE_FROM: dateFrom,
        DATE_TO: dateTo
      };
      if (method && method !== 'all') action.PMT_METHOD = method;

      const res = await blobticService.executeZoomRequest({ ZoomKey: 'Blobtic_Print', Action: action }, false);
      const data = (Array.isArray(res.data) ? (res.data as any)[0] : res.data) as any;
      if (res.success && data && Array.isArray(data.payments)) {
        return { payments: data.payments, total: Number(data.total) || 0 };
      }
    } catch (err) {
      console.error('Failed to load print receipts:', err);
    }
    return null;
  },

  getPrintDailyCash: async (
    reportDate: string,
    method?: 'all' | 'Cash' | 'Card' | 'Transfer'
  ): Promise<{ payments: any[]; totals: Record<string, number> } | null> => {
    try {
      const action: { OKey: string; [key: string]: string | number } = {
        OKey: 'Blobtic_Print,academy_print,load_print_daily_cash',
        DATE: reportDate
      };
      if (method && method !== 'all') action.PMT_METHOD = method;

      const res = await blobticService.executeZoomRequest({ ZoomKey: 'Blobtic_Print', Action: action }, false);
      const data = (Array.isArray(res.data) ? (res.data as any)[0] : res.data) as any;
      if (res.success && data && Array.isArray(data.payments)) {
        return {
          payments: data.payments,
          totals: {
            cash: Number(data.total_cash) || 0,
            card: Number(data.total_card) || 0,
            transfer: Number(data.total_transfer) || 0,
            grand: Number(data.total) || 0,
            count: data.payments.length
          }
        };
      }
    } catch (err) {
      console.error('Failed to load print daily cash:', err);
    }
    return null;
  },

  // ─────────────────────────────────────────────────────────────────────
  // boubaker 26/08/2026 - Envoi serveur des relances de dettes par email (Blobtic_Academy,academy_invoice,send_debt_reminder)
  // ─────────────────────────────────────────────────────────────────────
  sendBulkDebtReminder: async (
    studentIds: number[],
    language?: string
  ): Promise<{ success: boolean; message?: string; data?: { sentCount: number; failedCount: number; skippedCount: number; total: number } }> => {
    try {
      const action: { OKey: string; STUDENT_IDS: string; LANGUAGE?: string } = {
        OKey: 'Blobtic_Academy,academy_invoice,send_debt_reminder',
        STUDENT_IDS: studentIds.join(','),
        LANGUAGE: language || 'fr'
      };
      const res = await blobticService.executeZoomRequest({ ZoomKey: 'Blobtic_Academy', Action: action }, false);
      const data = (Array.isArray(res.data) ? (res.data as any)[0] : res.data) as any;
      return {
        success: !!res?.success,
        message: res?.message,
        data: data || undefined
      };
    } catch (err: any) {
      console.error('Failed to send bulk debt reminder:', err);
      return { success: false, message: err?.message || 'Debt reminder sending failed' };
    }
  },

  // ─────────────────────────────────────────────────────────────────────
  // boubaker 25/08/2026 - Indicateur : trace des envois mail (TABP_MAIL_LOG via Blobtic_Indicateur)
  // ─────────────────────────────────────────────────────────────────────
  getMailLogs: async (
    dateFrom: string,
    dateTo: string,
    filters?: { status?: string; to?: string; type?: string }
  ): Promise<any[] | null> => {
    try {
      const action: { OKey: string; [key: string]: string | number } = {
        OKey: 'Blobtic_Indicateur,academy_indicateur,load_mail_log',
        DATE_FROM: dateFrom,
        DATE_TO: dateTo
      };
      if (filters?.status && filters.status !== 'all') action.EML_STATUS = filters.status;
      if (filters?.to) action.EML_TO = filters.to;
      if (filters?.type && filters.type !== 'all') action.EML_TYPE = filters.type;

      const res = await blobticService.executeZoomRequest({ ZoomKey: 'Blobtic_Indicateur', Action: action }, false);
      const data = (Array.isArray(res.data) ? (res.data as any)[0] : res.data) as any;
      if (res.success && data && Array.isArray(data.logs)) {
        return data.logs;
      }
    } catch (err) {
      console.error('Failed to load mail logs:', err);
    }
    return null;
  },

  // ─────────────────────────────────────────────────────────────────────
  // Demandes de remise sur facture (TABP_ACADEMY_DISCOUNT_REQUEST)
  // ─────────────────────────────────────────────────────────────────────
  getDiscountRequests: async (useCache: boolean = true): Promise<DiscountRequest[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_discount_request,Select' },
        useCache
      }, useCache);
      if (res.success && Array.isArray(res.data)) {
        return res.data.map(mapDiscountRequest);
      }
    } catch (err) {
      console.error('Failed to load discount requests from DB:', err);
    }
    return null;
  },

  createDiscountRequest: async (req: Partial<DiscountRequest>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount_request,Insert',
        INV_IDF: req.invoice_id,
        STD_IDF: req.student_id,
        ...(req.discount_id ? { DSC_IDF: req.discount_id } : {}),
        ...(req.discount_code ? { REQ_DISCOUNT_CODE: req.discount_code } : {}),
        REQ_DISCOUNT_PERCENT: req.discount_percent ?? 0,
        REQ_DISCOUNT_AMOUNT: req.discount_amount ?? 0,
        REQ_ORIGINAL_AMOUNT: req.original_amount ?? 0,
        REQ_NEW_AMOUNT: req.new_amount ?? 0,
        REQ_JUSTIFICATION: req.justification || '',
        REQ_STATUS: 'Pending',
        ...(req.dos_idf ? { DOS_IDF: req.dos_idf } : {})
      }
    });
  },

  approveDiscountRequest: async (requestId: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount_request,Approve',
        REQ_IDF: requestId
      }
    });
  },

  rejectDiscountRequest: async (requestId: number, comment?: string) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount_request,Reject',
        REQ_IDF: requestId,
        REVIEW_COMMENT: comment || ''
      }
    });
  },

  deleteDiscountRequest: async (requestId: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_discount_request,Delete',
        REQ_IDF: requestId
      }
    });
  },

  // 17. Email Templates (TABP_MAIL_TEMPLATE)
  getEmailTemplates: async (useCache = true): Promise<EmailTemplate[] | null> => {
    try {
      const res = await blobticService.executeZoomRequest({
        ZoomKey: 'Blobtic_Academy',
        Action: { OKey: 'Blobtic_Academy,academy_email_template,Select' },
        useCache
      }, useCache);
      if (res.success && Array.isArray(res.data)) {
        return res.data.map(mapEmailTemplate);
      }
    } catch (err) {
      console.error('Failed to load email templates from DB:', err);
    }
    return null;
  },

  createEmailTemplate: async (tpl: Partial<EmailTemplate>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_email_template,Insert',
        TPL_CODE: tpl.code || `${tpl.mail_type}_${(tpl.lang || 'FR').toUpperCase()}`,
        TPL_MAIL_TYPE: tpl.mail_type || 'GENERAL',
        TPL_LANG: tpl.lang || 'fr',
        TPL_NAME: tpl.name || '',
        TPL_SUBJECT: tpl.subject || '',
        TPL_BODY: tpl.body || '',
        TPL_TAGS: tpl.tags || '',
        TPL_IS_ACTIVE: tpl.is_active !== false ? 1 : 0,
        TPL_DESCRIPTION: tpl.description || '',
        ...(tpl.dos_idf ? { DOS_IDF: tpl.dos_idf } : {})
      }
    });
  },

  updateEmailTemplate: async (id: number, tpl: Partial<EmailTemplate>) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_email_template,Update',
        TPL_IDF: id,
        TPL_CODE: tpl.code || `${tpl.mail_type}_${(tpl.lang || 'FR').toUpperCase()}`,
        TPL_MAIL_TYPE: tpl.mail_type || 'GENERAL',
        TPL_LANG: tpl.lang || 'fr',
        TPL_NAME: tpl.name || '',
        TPL_SUBJECT: tpl.subject || '',
        TPL_BODY: tpl.body || '',
        TPL_TAGS: tpl.tags || '',
        TPL_IS_ACTIVE: tpl.is_active !== false ? 1 : 0,
        TPL_DESCRIPTION: tpl.description || '',
        ...(tpl.dos_idf ? { DOS_IDF: tpl.dos_idf } : {})
      }
    });
  },

  deleteEmailTemplate: async (id: number) => {
    return await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_email_template,Delete',
        TPL_IDF: id
      }
    });
  },

  // ── Subscription & SATIM ──────────────────────────────────────────────────
  getSubscriptionPlans: async (): Promise<AcademySubscriptionPlan[]> => {
    const res = await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_subscription_plan,Select'
      }
    });
    return ((res?.data as any[]) || []).map(mapSubscriptionPlan);
  },

  getSubscriptionUsage: async (dosId?: number): Promise<SubscriptionUsage> => {
    const res = await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_subscription_usage,Select',
        ...(dosId ? { DOS_IDF: dosId } : {})
      }
    });
    return res?.data as SubscriptionUsage;
  },

  getSubscriptionInvoices: async (dosId?: number): Promise<AcademySubscriptionInvoice[]> => {
    const res = await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_subscription_invoice,Select',
        ...(dosId ? { DOS_IDF: dosId } : {})
      }
    });
    return ((res?.data as any[]) || []).map(mapSubscriptionInvoice);
  },

  upgradeSubscription: async (params: { planId: number; billingCycle: 'monthly' | 'yearly'; paymentMethod?: string; transactionId?: string; dosId?: number }) => {
    const res = await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_subscription_upgrade,Execute',
        PLN_IDF: params.planId,
        SUB_BILLING_CYCLE: params.billingCycle,
        SUB_PAYMENT_METHOD: params.paymentMethod || 'SATIM',
        ...(params.transactionId ? { TRANSACTION_ID: params.transactionId } : {}),
        ...(params.dosId ? { DOS_IDF: params.dosId } : {})
      }
    });
    return res?.data;
  },

  initiateSatimPayment: async (params: { planId: number; billingCycle: 'monthly' | 'yearly'; dosId?: number }): Promise<SatimInitiateResponse> => {
    const res = await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_satim_initiate,Execute',
        PLN_IDF: params.planId,
        SUB_BILLING_CYCLE: params.billingCycle,
        ...(params.dosId ? { DOS_IDF: params.dosId } : {})
      }
    });
    return res?.data as SatimInitiateResponse;
  },

  confirmSatimPayment: async (params: { orderId: string; cardBrand: string; last4: string; planId: number; billingCycle: 'monthly' | 'yearly'; dosId?: number }): Promise<SatimConfirmResponse> => {
    const res = await blobticService.executeZoomRequest({
      ZoomKey: 'Blobtic_Academy',
      Action: {
        OKey: 'Blobtic_Academy,academy_satim_confirm,Execute',
        orderId: params.orderId,
        cardBrand: params.cardBrand,
        last4: params.last4,
        PLN_IDF: params.planId,
        SUB_BILLING_CYCLE: params.billingCycle,
        ...(params.dosId ? { DOS_IDF: params.dosId } : {})
      }
    });
    return res?.data as SatimConfirmResponse;
  },

  // ==========================================================================
  // ACADEMY PUBLIC PORTAL API (STUDENT & TEACHER)
  // ==========================================================================
  portalStudentLogin: async (login: string, password: string): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_student_login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password })
    });
    return await res.json();
  },

  portalStudentSelectAcademy: async (loginOrToken: string, dos_idf: number, isParent?: boolean): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_student_select_academy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: loginOrToken,
        loginIdentifier: loginOrToken,
        login: loginOrToken,
        dos_idf,
        dosId: dos_idf,
        isParent
      })
    });
    return await res.json();
  },

  portalStudentGetData: async (token: string): Promise<any> => {
    if (!token || token === 'undefined' || token === 'null') {
      return { success: false, message: 'UNAUTHORIZED', error: 'UNAUTHORIZED' };
    }
    const res = await fetch(`/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_student_get_data?token=${encodeURIComponent(token)}`);
    return await res.json();
  },

  portalStudentSaveAbsenceJustification: async (
    token: string,
    sessionIdOrPayload: number | { session_id: number; reason?: string; justification_reason?: string; doc_name?: string; doc_base64?: string; student_id?: number },
    reason?: string,
    doc_name?: string,
    doc_base64?: string,
    student_id?: number
  ): Promise<any> => {
    let ssnId: number;
    let rsn: string;
    let dName: string | undefined;
    let dBase64: string | undefined;
    let stdId: number | undefined;

    if (typeof sessionIdOrPayload === 'object' && sessionIdOrPayload !== null) {
      ssnId = sessionIdOrPayload.session_id;
      rsn = sessionIdOrPayload.reason || sessionIdOrPayload.justification_reason || '';
      dName = sessionIdOrPayload.doc_name;
      dBase64 = sessionIdOrPayload.doc_base64;
      stdId = sessionIdOrPayload.student_id;
    } else {
      ssnId = Number(sessionIdOrPayload);
      rsn = reason || '';
      dName = doc_name;
      dBase64 = doc_base64;
      stdId = student_id;
    }

    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_student_save_absence_justification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        session_id: ssnId,
        sessionId: ssnId,
        student_id: stdId,
        studentId: stdId,
        reason: rsn,
        justification_reason: rsn,
        doc_name: dName,
        docName: dName,
        doc_base64: dBase64,
        docBase64: dBase64
      })
    });
    return await res.json();
  },

  portalTeacherLogin: async (login: string, password: string): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password })
    });
    return await res.json();
  },

  portalTeacherSelectAcademy: async (loginOrToken: string, dos_idf: number): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_select_academy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: loginOrToken,
        loginIdentifier: loginOrToken,
        login: loginOrToken,
        dos_idf,
        dosId: dos_idf
      })
    });
    return await res.json();
  },

  portalTeacherGetData: async (token: string): Promise<any> => {
    if (!token || token === 'undefined' || token === 'null') {
      return { success: false, message: 'UNAUTHORIZED', error: 'UNAUTHORIZED' };
    }
    const res = await fetch(`/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_get_data?token=${encodeURIComponent(token)}`);
    return await res.json();
  },

  portalTeacherSaveAttendance: async (token: string, session_id: number, student_id: number, status: string, notes?: string): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_save_attendance', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        session_id,
        sessionId: session_id,
        student_id,
        studentId: student_id,
        status,
        notes
      })
    });
    return await res.json();
  },

  portalTeacherScanBadge: async (token: string, session_id: number, badge_code: string): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_scan_badge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        session_id,
        sessionId: session_id,
        badge_code,
        badgeCode: badge_code
      })
    });
    return await res.json();
  },

  portalTeacherSaveAbsenceJustification: async (
    token: string,
    attendance_id: number,
    reason: string,
    doc_name?: string,
    doc_base64?: string,
    session_id?: number,
    student_id?: number
  ): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_save_absence_justification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        attendance_id,
        attendanceId: attendance_id,
        reason,
        doc_name,
        docName: doc_name,
        doc_base64,
        docBase64: doc_base64,
        session_id,
        sessionId: session_id,
        student_id,
        studentId: student_id
      })
    });
    return await res.json();
  },

  portalTeacherSubmitAbsenceRequest: async (token: string, session_id: number, start_date: string, end_date: string, reason: string): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_submit_absence_request', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        session_id,
        sessionId: session_id,
        start_date,
        startDate: start_date,
        end_date,
        endDate: end_date,
        reason
      })
    });
    return await res.json();
  },

  portalTeacherGetAbsenceRequests: async (token: string): Promise<any> => {
    const res = await fetch(`/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_teacher_get_absence_requests?token=${encodeURIComponent(token)}`);
    return await res.json();
  },

  portalSsoVerify: async (provider: string, ssoToken: string, portalType: 'student' | 'teacher'): Promise<any> => {
    const res = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/portal_sso_verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, sso_token: ssoToken, portal_type: portalType })
    });
    return await res.json();
  }
};

export const mapEmailTemplate = (dto: any): EmailTemplate => ({
  id: Number(dto.TPL_IDF),
  uid: dto.TPL_UIDF,
  dos_idf: dto.DOS_IDF ? Number(dto.DOS_IDF) : undefined,
  dos_code: dto.DOS_CODE || undefined,
  dos_lib: dto.DOS_LIB || undefined,
  code: dto.TPL_CODE || '',
  mail_type: dto.TPL_MAIL_TYPE || '',
  lang: dto.TPL_LANG || 'fr',
  name: dto.TPL_NAME || '',
  subject: dto.TPL_SUBJECT || '',
  body: dto.TPL_BODY || '',
  tags: dto.TPL_TAGS || '',
  is_active: dto.TPL_IS_ACTIVE === true || dto.TPL_IS_ACTIVE === 1 || dto.TPL_IS_ACTIVE === '1',
  description: dto.TPL_DESCRIPTION || '',
  user_idf: dto.USER_IDF ? Number(dto.USER_IDF) : undefined,
  user_lib: dto.USER_LIB || undefined,
  last_updt: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

export const mapSubscriptionPlan = (dto: any): AcademySubscriptionPlan => ({
  PLN_IDF: Number(dto.PLN_IDF),
  PLN_CODE: dto.PLN_CODE || '',
  PLN_NAME: dto.PLN_NAME || '',
  PLN_DESCRIPTION: dto.PLN_DESCRIPTION || '',
  PLN_PRICE_MONTHLY: Number(dto.PLN_PRICE_MONTHLY || 0),
  PLN_PRICE_YEARLY: Number(dto.PLN_PRICE_YEARLY || 0),
  PLN_CURRENCY: dto.PLN_CURRENCY || 'DZD',
  PLN_TRIAL_DAYS: Number(dto.PLN_TRIAL_DAYS || 0),
  PLN_MAX_STUDENTS: Number(dto.PLN_MAX_STUDENTS ?? -1),
  PLN_MAX_TEACHERS: Number(dto.PLN_MAX_TEACHERS ?? -1),
  PLN_MAX_AGENCIES: Number(dto.PLN_MAX_AGENCIES ?? 1),
  PLN_MAX_USERS: Number(dto.PLN_MAX_USERS ?? 1),
  PLN_SMS_CREDITS: Number(dto.PLN_SMS_CREDITS || 0),
  PLN_STORAGE_MB: Number(dto.PLN_STORAGE_MB || 0),
  PLN_REPORTS_ENABLED: dto.PLN_REPORTS_ENABLED === true || dto.PLN_REPORTS_ENABLED === 1 || dto.PLN_REPORTS_ENABLED === '1',
  PLN_FEATURES_JSON: dto.PLN_FEATURES_JSON || '[]',
  PLN_BADGE: dto.PLN_BADGE || undefined,
  PLN_COLOR: dto.PLN_COLOR || '#6366f1',
  PLN_ORDER: Number(dto.PLN_ORDER || 0),
  PLN_IS_ACTIVE: dto.PLN_IS_ACTIVE === true || dto.PLN_IS_ACTIVE === 1 || dto.PLN_IS_ACTIVE === '1',
});

export const mapSubscription = (dto: any): AcademySubscription => ({
  SUB_IDF: Number(dto.SUB_IDF),
  DOS_IDF: Number(dto.DOS_IDF),
  DOS_CODE: dto.DOS_CODE || undefined,
  DOS_LIB: dto.DOS_LIB || undefined,
  PLN_IDF: Number(dto.PLN_IDF),
  PLN_CODE: dto.PLN_CODE || '',
  PLN_NAME: dto.PLN_NAME || '',
  SUB_STATUS: dto.SUB_STATUS || 'active',
  SUB_BILLING_CYCLE: dto.SUB_BILLING_CYCLE || 'monthly',
  SUB_START_DATE: dto.SUB_START_DATE ? toLocalDateStr(dto.SUB_START_DATE) : '',
  SUB_END_DATE: dto.SUB_END_DATE ? toLocalDateStr(dto.SUB_END_DATE) : '',
  SUB_TRIAL_END_DATE: dto.SUB_TRIAL_END_DATE ? toLocalDateStr(dto.SUB_TRIAL_END_DATE) : undefined,
  SUB_AUTO_RENEW: dto.SUB_AUTO_RENEW === true || dto.SUB_AUTO_RENEW === 1 || dto.SUB_AUTO_RENEW === '1',
  SUB_PAYMENT_METHOD: dto.SUB_PAYMENT_METHOD || 'SATIM',
  SUB_AMOUNT_PAID: Number(dto.SUB_AMOUNT_PAID || 0),
  SUB_CURRENCY: dto.SUB_CURRENCY || 'DZD',
  SUB_NOTES: dto.SUB_NOTES || '',
  LAST_UPDT: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
  USER_IDF: dto.USER_IDF ? Number(dto.USER_IDF) : undefined,
  USER_LIB: dto.USER_LIB || undefined,
});

export const mapSubscriptionInvoice = (dto: any): AcademySubscriptionInvoice => ({
  INV_IDF: Number(dto.INV_IDF),
  SUB_IDF: Number(dto.SUB_IDF),
  DOS_IDF: Number(dto.DOS_IDF),
  DOS_CODE: dto.DOS_CODE || undefined,
  DOS_LIB: dto.DOS_LIB || undefined,
  INV_NUMBER: dto.INV_NUMBER || '',
  INV_DATE: dto.INV_DATE ? toLocalDateStr(dto.INV_DATE) : '',
  INV_DUE_DATE: dto.INV_DUE_DATE ? toLocalDateStr(dto.INV_DUE_DATE) : undefined,
  INV_AMOUNT: Number(dto.INV_AMOUNT || 0),
  INV_CURRENCY: dto.INV_CURRENCY || 'DZD',
  INV_STATUS: dto.INV_STATUS || 'PENDING',
  INV_PAYMENT_METHOD: dto.INV_PAYMENT_METHOD || undefined,
  INV_TRANSACTION_ID: dto.INV_TRANSACTION_ID || undefined,
  INV_NOTES: dto.INV_NOTES || '',
  LAST_UPDT: dto.LAST_UPDT ? new Date(dto.LAST_UPDT).toLocaleString('fr-DZ') : undefined,
});

